/**
 * Wing beat is ~40-50Hz (PMC3311889) — past what any display resolves — so the wings
 * render as a translucent, blurred envelope (CSS transform/opacity only) rather than
 * discrete flaps. Hovering traces a lemniscate (figure-8) station-keeping pattern (Royal
 * Society) instead of a plain scale pulse.
 *
 * Flight speed (FORWARD_SPEED_PX_S / the `speed` option) is tuned for legibility
 * as a coach mark — not a physically realistic hummingbird airspeed.
 *
 * Single shared RAF loop drives all animation states — no nested loops. Every coordinate
 * the bird holds is viewport-space, because it flies in the top layer and the top layer does
 * not scroll with the document; the active leg's endpoints are therefore re-measured from
 * their own elements inside that same loop rather than subscribed to.
 *
 * State machine:
 * - idle: waiting for delay
 * - flying: moving between waypoints, oriented to heading with smoothed banking
 * - hovering: figure-8 + micro-corrections + breathing pulse, heading held
 * - spiraling: approaching black hole
 * - absorbed: being sucked into black hole, then the collapse that outlives the bird
 * - attending: keeping a surface company while work runs on it (the `attend` flight)
 * - done: cleanup complete
 */

import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import type { ElementPart } from 'lit'
import type { PartInfo } from 'lit/directive.js'
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, Subject, forkJoin, from, of, timer, fromEvent, combineLatest } from 'rxjs'
import {
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	takeUntil,
	tap,
	take,
	startWith,
	catchError,
} from 'rxjs/operators'
import { discover } from '@schmancy/mixins/discovery.service'
import { ThemeWhereAreYou, ThemeHereIAm } from '@schmancy/theme/theme.events'

// Physics constants
const FORWARD_SPEED_PX_S = 90 // Pixels per second (default; overridable via HummingbirdOptions.speed)
const PAUSE_DURATION_MS = 600 // Brief pause at each waypoint
const HOST_CLEARANCE_PX = 60 // How far clear of the host's edge a flight enters and (with no waypoints) leaves
const BIRD_RADIUS = 12 // Mote is 24px, centered on its waypoint — dainty by design; the trailing arcs are a faint motion hint, not a legibility feature needing more canvas
const HEADING_SMOOTHING = 0.18 // Fraction of the remaining heading delta closed per frame — settles a reversed leg in ~6 frames (~100ms at 60fps) instead of snapping
const BIRD_EXIT_MS = 400 // Long enough to read as the bird leaving, short enough that the control it was sitting over is its reader's again in one beat
const SNITCH_LEG_MS = 1300 // The snitch flies every leg in this one eased arc, whatever its length
const SNITCH_LIFT_PX = 65 // How high the snitch's arc rises mid-leg
const MOUSE_PRESS_PX = 32 // How far a press on the bird reaches by mouse, past its 24px artwork
const TOUCH_PRESS_PX = 44 // And where a finger can press it
const PAGE_SETTLES_MS = 160 // After a scroll or a resize the bird may no longer be over what the person aimed at

// Reading budget for a labelled waypoint's hover
const LABEL_FADE_IN_MS = 250 // The pill's own fade-in; nothing is legible until it ends, so the reading budget starts after it
const LABEL_FADE_OUT_MS = 200 // The pill's fade-out, outside the budget: the caption has been read by the time it runs
const LABEL_SWAP_MS = 200 // A working line giving way to the next: long enough to read as one caption changing its words, short enough that the new step is legible within a beat
const LABEL_ONSET_MS = 400 // Saccade onto the pill plus the first fixation, before any word is taken in — without it a two-word caption gets a blink
// A "word" here is the 5-character unit (trailing space included) reading rates are measured in, not a
// whitespace token, so the metric is language-neutral: a German compound costs what its length costs.
const CHARS_PER_STANDARD_WORD = 5
// 200 wpm — the slow end of the adult-prose band, because a coach caption is glanced at mid-task by a
// reader whose job is the control underneath it rather than read the way continuous prose is.
const STANDARD_WORDS_PER_SECOND = 200 / 60
// Smoke alarm on the length of a caption that holds up a flight, not a timing control: it sits just above the
// longest caption shipped, so it truncates nothing written today and trips when copy outgrows a coach mark. A
// working line holds nothing up and its words are whatever the work reports, so it takes its whole reading time.
const LABEL_DWELL_MAX_MS = 4500

/** How long a line takes to read: the pill's fade, a fixation allowance, then its length at 200 wpm. */
const readingTimeMs = (text: string) =>
	LABEL_FADE_IN_MS + LABEL_ONSET_MS + (text.trim().length / CHARS_PER_STANDARD_WORD / STANDARD_WORDS_PER_SECOND) * 1000

/** How long a caption holds the flight it stops: a waypoint's hover, a done bird's report. */
const captionDwellMs = (text: string) => Math.min(LABEL_DWELL_MAX_MS, readingTimeMs(text))

// The attend loop stands this far off the surface on every side — clear of a focus ring or a card's
// shadow, close enough that the bird reads as belonging to that surface and not to its neighbour.
const ATTEND_CLEARANCE_PX = 16
const ATTEND_FRAME_CAP_MS = 50 // A frame gap past this (a paused tab, a long task) advances the loop one frame's worth, not a jump
const LOOP_SIDES: ResolvedLabelPlacement[] = ['top', 'right', 'bottom', 'left']

/** The attend loop's length: the surface's own perimeter plus the four quarter-circle corners. */
const loopLength = (rect: DOMRect) => 2 * (rect.width + rect.height) + 2 * Math.PI * ATTEND_CLEARANCE_PX

/**
 * The point `s` px of arc clockwise from the top edge's left end, on the loop the attend flight
 * circles: `rect` grown by ATTEND_CLEARANCE_PX, its corners rounded to that same radius, so every
 * point stands exactly that far off the surface. `side` is the edge the point stands outside — the
 * side a caption opens towards so it never covers the surface being worked on.
 */
function loopPoint(rect: DOMRect, s: number): { x: number; y: number; side: ResolvedLabelPlacement } {
	const c = ATTEND_CLEARANCE_PX
	const arc = (Math.PI * c) / 2
	const legs = [rect.width, arc, rect.height, arc, rect.width, arc, rect.height, arc]
	const perimeter = loopLength(rect)
	let d = ((s % perimeter) + perimeter) % perimeter
	let leg = 0
	while (leg < 7 && d > legs[leg]) d -= legs[leg++]
	const edge = leg >> 1
	if (leg % 2 === 0)
		return [
			{ x: rect.left + d, y: rect.top - c, side: 'top' as const },
			{ x: rect.right + c, y: rect.top + d, side: 'right' as const },
			{ x: rect.right - d, y: rect.bottom + c, side: 'bottom' as const },
			{ x: rect.left - c, y: rect.bottom - d, side: 'left' as const },
		][edge]
	const corner = [
		[rect.right, rect.top],
		[rect.right, rect.bottom],
		[rect.left, rect.bottom],
		[rect.left, rect.top],
	][edge]
	const angle = (edge - 1) * (Math.PI / 2) + d / c
	return {
		x: corner[0] + Math.cos(angle) * c,
		y: corner[1] + Math.sin(angle) * c,
		side: LOOP_SIDES[d < arc / 2 ? edge : (edge + 1) % 4],
	}
}

// The `on` flight works one surface the way a hummingbird works one flower: a drift over its box
// built from the thinking bubble's harmonic field. Each axis sums three incommensurate sines whose
// phases also wobble slowly (that field's `0.3·sin(0.37·θ + 2.1·φ)` term), and each x harmonic is
// Kuramoto-coupled to its y partner below the locking threshold, so the pair keeps slipping in and
// out of step and the path never closes into a loop. Frequencies are rad/ms; at the amplitude cap the
// bird's median speed is ~20–30px/s and its fastest ~70px/s — a bird browsing, not searching.
const WANDER_HARMONICS = [
	{ x: 0.00031, y: 0.00037, weight: 1, phase: 0 },
	{ x: 0.00053, y: 0.00061, weight: 0.55, phase: 1.2 },
	{ x: 0.00089, y: 0.00079, weight: 0.3, phase: 2.5 },
]
const WANDER_WEIGHT = WANDER_HARMONICS.reduce((sum, h) => sum + h.weight, 0)
// Coupling as a fraction of each pair's detuning: under half of it the pair cannot lock, so its phase
// difference lingers near quadrature and then slips — intermittent, never periodic.
const WANDER_COUPLING = 0.4
// The harmonics swing at most this far from their centre, so a whole ticket is explored at the same
// calm pace as a title line; past it the centre itself drifts over the rest of the box this slowly.
const WANDER_SWING_CAP_PX = 100
const WANDER_DRIFT = { x: 0.000113, y: 0.000097 }
// Where the drift is slow the wander's own clock slows further — the bird stops to feed — but never
// below this rate, and the hover figure-8 keeps running on real time underneath it.
const WANDER_FEED_RATE = 0.2
const WANDER_FEED_BELOW = 0.3 // Of the box's top speed; below it the clock slows toward the feed rate
// The 24px bird stays over the surface: its radius, plus the figure-8's 4.5px swing across and the
// larger of that figure-8's 2.5px and the snitch's 4px bob down.
const WANDER_INSET = { x: 12 + 5, y: 12 + 4 }

/** The wander's state: each harmonic's phase per axis, the drifting centre's phases, and the clock's rate. */
interface Wander {
	x: number[]
	y: number[]
	driftX: number
	driftY: number
	rate: number
}

/** The swing each axis has inside `rect`, and the drift range its centre roams beyond the swing. */
function wanderReach(rect: DOMRect) {
	const halfX = Math.max(0, rect.width / 2 - WANDER_INSET.x)
	const halfY = Math.max(0, rect.height / 2 - WANDER_INSET.y)
	const swingX = Math.min(halfX, WANDER_SWING_CAP_PX)
	const swingY = Math.min(halfY, WANDER_SWING_CAP_PX)
	return { swingX, swingY, driftX: halfX - swingX, driftY: halfY - swingY }
}

/**
 * The wander's point inside `rect`. Each axis is a weighted mean of sines, so it is bounded by its
 * swing, and the centre by its drift range — the two together never leave the inset box. A box
 * smaller than the bird has neither and the point is its centre.
 */
function wanderPoint(rect: DOMRect, wander: Wander): { x: number; y: number } {
	const reach = wanderReach(rect)
	const axis = (phases: number[]) =>
		WANDER_HARMONICS.reduce(
			(sum, h, i) => sum + h.weight * Math.sin(phases[i] + 0.3 * Math.sin(0.37 * phases[i] + 2.1 * h.phase)),
			0,
		) / WANDER_WEIGHT
	return {
		x: rect.left + rect.width / 2 + reach.driftX * Math.sin(wander.driftX) + reach.swingX * axis(wander.x),
		y: rect.top + rect.height / 2 + reach.driftY * Math.sin(wander.driftY) + reach.swingY * axis(wander.y),
	}
}

/** Advances the wander by `ms` of its own clock, then retunes that clock to how fast the point moved. */
function advanceWander(rect: DOMRect, wander: Wander, ms: number): void {
	const before = wanderPoint(rect, wander)
	const step = ms * wander.rate
	WANDER_HARMONICS.forEach((h, i) => {
		const coupling = WANDER_COUPLING * Math.abs(h.x - h.y)
		const x = wander.x[i]
		const y = wander.y[i]
		wander.x[i] += (h.x + coupling * Math.sin(y - x)) * step
		wander.y[i] += (h.y + coupling * Math.sin(x - y)) * step
	})
	wander.driftX += WANDER_DRIFT.x * step
	wander.driftY += WANDER_DRIFT.y * step
	const reach = wanderReach(rect)
	const meanFrequency = (axis: 'x' | 'y') => WANDER_HARMONICS.reduce((sum, h) => sum + h.weight * h[axis], 0) / WANDER_WEIGHT
	const topSpeed = Math.hypot(
		reach.swingX * meanFrequency('x') + reach.driftX * WANDER_DRIFT.x,
		reach.swingY * meanFrequency('y') + reach.driftY * WANDER_DRIFT.y,
	)
	if (!step || !topSpeed) return
	const after = wanderPoint(rect, wander)
	const s = Math.min(1, Math.hypot(after.x - before.x, after.y - before.y) / step / (WANDER_FEED_BELOW * topSpeed))
	wander.rate += (WANDER_FEED_RATE + (1 - WANDER_FEED_RATE) * s * s * (3 - 2 * s) - wander.rate) * Math.min(1, ms * 0.0036)
}

/** Which gesture a waypoint teaches, and so which of `tapWave`'s two inks its mark plays back. */
type WaypointTone = 'add' | 'remove'

// The waypoint's mark is the control's own tap feedback, played back. These numbers are the wave
// `tapWave` (./tap-wave.ts) renders on a press — the same curve, blur, peak, and the same ink per
// gesture — so what the coach shows over a half is what the reader's own finger would have produced
// there. That file owns the original; an extraction both can call is owed, and until it lands this
// copy is free to drift from it.
const TAP_WAVE_MS = 540
const TAP_WAVE_PEAK = 0.55
const TAP_WAVE_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)'
const TAP_WAVE_COLOR: Record<WaypointTone, string> = {
	add: 'var(--schmancy-sys-color-primary-default)',
	remove: 'var(--schmancy-sys-color-surface-onVariant)',
}
// One demonstrated tap per beat. A wave is spent by 540ms, and a tap the reader is meant to count as
// a tap has to settle before the next one starts, so the beat is the wave plus a rest of its own
// length again — and 1200ms is the slowest beat that still fits two whole taps inside the shortest
// dwell the coach holds, 1850ms: one as the bird settles, one at 1200 that is done by 1740.
const TAP_WAVE_BEAT_MS = 1200

// Animation states
type AnimationState = 'idle' | 'flying' | 'hovering' | 'spiraling' | 'absorbed' | 'attending' | 'done'

/**
 * Element reference for waypoints/target:
 * - CSS selector (e.g., '#app-card-melanie', '.my-class') - resolved via `discover()`, a shadow-piercing descendant walk
 * - Component tag name (e.g., 'schmancy-fancy') - same walk; a bare tag name is a valid selector too
 * - HTMLElement reference directly
 */
type ElementRef = string | HTMLElement

/** Waypoint with optional duration control */
interface Waypoint {
	ref: ElementRef
	/** Surface receiving the demonstrated tap wave; defaults to the bird waypoint. */
	waveSurface?: ElementRef
	/**
	 * Floor on the hover at this waypoint (ms). An unlabelled waypoint holds exactly this long; a
	 * labelled one holds for whichever is longer, this or its own caption's reading time (the pill's
	 * fade, a fixation allowance, then the caption's length at 200 wpm, capped at 4.5s).
	 */
	duration?: number
	label?: string // Floating text caption shown near the bird while it hovers here (e.g. "Tap to add")
	/**
	 * Which gesture this waypoint teaches, so its mark is played back in that gesture's own ink —
	 * `tapWave`'s primary for an add, its neutral for a remove. Defaults to 'add'.
	 */
	tone?: WaypointTone
}

/**
 * The span and curve a launched bird grows on, as Web Animations timing: collapse the surface the
 * bird replaces into its `start` on exactly this, from the frame `working` is sent, and one shape
 * becomes the other.
 */
export const hummingbirdLaunch = { duration: 300, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' } as const

/** Where a launched bird appears: an element's centre (read when `working` arrives), or a viewport point's or rect's. */
export type HummingbirdAttendStart = Element | { x: number; y: number; width?: number; height?: number }

/** A place on the page, in viewport pixels. The bird takes it as given: a new place is a new flight. */
export type HummingbirdBox = { x: number; y: number; width: number; height: number }

/** Names the layer holding the bird and everything it spawns, so a read of the page can leave it out. */
export const HUMMINGBIRD_LAYER = '[data-schmancy-hummingbird]'

const boxOf = (around: Element | HummingbirdBox): DOMRect =>
	around instanceof Element ? around.getBoundingClientRect() : new DOMRect(around.x, around.y, around.width, around.height)

/** The part of a surface in the window, which is all a bird over it can be seen on; all of it when none of it is in. */
const inView = (rect: DOMRect): DOMRect => {
	const left = Math.max(rect.left, 0)
	const top = Math.max(rect.top, 0)
	const width = Math.min(rect.right, window.innerWidth) - left
	const height = Math.min(rect.bottom, window.innerHeight) - top
	return width > 0 && height > 0 ? new DOMRect(left, top, width, height) : rect
}

const samePlace = (a: Element | HummingbirdBox | undefined, b: Element | HummingbirdBox | undefined): boolean =>
	a === b ||
	(!!a && !!b && !(a instanceof Element) && !(b instanceof Element) && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height)

/** Where the work the bird is attending stands. */
export type HummingbirdAttendStatus =
	/**
	 * `start` launches a bird that is not yet on the surface from that point; a bird already there
	 * ignores it. `label` says what the work is doing now: it shows beside the bird for its reading time
	 * and is gone, the bird working on. New words show again; the same words do not.
	 */
	| { kind: 'working'; start?: HummingbirdAttendStart; label?: string }
	| { kind: 'done'; label: string }
	| { kind: 'failed'; label: string }

/** The attend flight: the bird keeps `around` company while work runs on it. */
export interface HummingbirdAttend {
	/**
	 * The surface the work is happening to; the bird circles it and reports beside it. An element is
	 * re-measured every frame; a box is a place on the page, in viewport pixels. Defaults to the host.
	 * Changing it, to another element or a box elsewhere, flies the bird from where it is to the new place.
	 */
	around?: Element | HummingbirdBox
	/**
	 * `'around'` (default) circles a loop 16px outside the surface. `'on'` stays over the surface
	 * itself: while working the bird wanders its box — inset so the whole bird stays over it, collapsing
	 * to a hover at the centre of a box smaller than the bird — on a drift that never visibly repeats,
	 * pausing where it slows as if to feed. `done` and `failed` hold the bird where it is, on the
	 * surface, with the label beside it. Under reduced motion the bird sits at the surface's centre.
	 */
	flight?: 'around' | 'on'
	/**
	 * `working` circles `around` until the status changes; with a `start`, a bird not yet there grows
	 * from nothing at that point on `hummingbirdLaunch`, then flies the shortest path onto the loop —
	 * under reduced motion it appears on the loop instead. A working `label` floats with the bird, a
	 * small gap above or below it (picked once per line, changing only when that side runs out of
	 * viewport), for its reading time at 200 wpm, with no cap, then fades out while the bird works on.
	 * Time out of sight is not spent. A new line crossfades into a pill still standing, or shows again,
	 * for its own time; the same words do not re-show. The pill is `aria-hidden`: the consumer
	 * announces progress in its own live region. `done` hovers at the edge the bird is on,
	 * shows the label for its reading time, then flies away. `failed` holds there with the label
	 * pinned as a button named by it, until the status changes. A change takes the bird on from
	 * wherever it is; a `working` or `failed` after the bird has left brings it back.
	 */
	status: Observable<HummingbirdAttendStatus>
	/** Called when the failed bird is pressed — click, Enter or Space. */
	onClick?: () => void
	/**
	 * Makes a working bird and its line pressable, as `onClick` does a failed one: a press on either, or
	 * Enter or Space on the line, calls `onClick`. `name` is the line's accessible name, since its words
	 * say what the work is doing rather than what the press opens. Naming it while the bird flies takes
	 * effect at once. The bird stays pressable after the line has gone, and a line keyboard focus stands
	 * on stays until focus leaves it. Without it a working bird takes no press and its line stays
	 * `aria-hidden`.
	 */
	workingPress?: { name: string; onClick: () => void }
}

/**
 * The bird itself is the thing to press, wherever it flies: a named button with a tab stop, reaching 32px by mouse and 44px
 * where a finger can press it, with no box of any kind. Everywhere else, presses go to the page.
 *
 * A press on a bird in flight passes through to the page, since the person was aiming at what is there. One just after the
 * page scrolled or resized is swallowed, since they may have aimed at something that has moved. While a modal is open the
 * bird is inert like the rest of the page.
 */
export interface HummingbirdPress {
	/** The bird's accessible name and tooltip: what a press opens. */
	label: string
	onPress: () => void
	/**
	 * What only the host can know. `'swallow'`: the place may have just changed, so a press does nothing and reaches nothing.
	 * `'pass'`: this is no time to press the bird, as in a capture, so presses go to the page and it leaves the tab order.
	 */
	refuse?: 'swallow' | 'pass' | undefined
	/** `aria-haspopup`: what a press opens. */
	haspopup?: 'dialog' | 'menu' | undefined
	/** `aria-expanded`: whether what it opens is open. */
	expanded?: boolean | undefined
	/** `aria-keyshortcuts`: the key that also presses it. */
	keyshortcuts?: string | undefined
}

/** Whether a press lands now, for a pointer: `'fly'` passes it through, while a key on the bird still counts. */
type PressState = 'take' | 'fly' | 'swallow' | 'pass'

interface HummingbirdOptions {
	/**
	 * Stand in one place until told otherwise: the bird grows there from nothing, wanders the place and
	 * flies when `at` changes, needing no status stream. `label` is a line beside it, shown for its reading
	 * time; new words show again. `attend` is ignored beside it. Under reduced motion it sits at the place's
	 * centre and a new place is reached without a flight.
	 */
	stand?: { at: Element | HummingbirdBox; label?: string }
	/** Makes the bird itself the thing to press. */
	press?: HummingbirdPress
	/** How wide the bird is, in pixels. Its artwork defaults to 24. */
	size?: number
	/**
	 * Keep a surface company while work runs on it, in place of `waypoints` and `home`. The bird
	 * plays no sound; which cue fits an outcome is the consumer's choice. Defaults `delay` to 0.
	 */
	attend?: HummingbirdAttend
	/** Bird artwork; motion and waypoint behavior are independent of appearance. */
	appearance?: 'mote' | 'snitch' | 'minimal'
	/** Upright flutter and an eased arcing flight; omitted preserves the standard coach motion. */
	motion?: 'snitch'
	/** Hold the first waypoint without an entrance flight or automatic departure. */
	stationary?: boolean
	/** Waypoints the bird visits in sequence */
	waypoints?: (ElementRef | Waypoint)[]
	/** Where the bird returns home (triggers black hole effect). If omitted, bird fades out after last waypoint. */
	home?: ElementRef
	/** Delay before starting flight (ms) */
	delay?: number
	/** Forward flight speed in px/sec. Defaults to 90. */
	speed?: number
	/** Whether the hummingbird should be playing. When false, cleans up and pauses. Defaults to true. */
	playing?: boolean
	/** Show animated connection lines between visited waypoints. Defaults to false. */
	showConnections?: boolean
	/** Which side of the bird the floating label sits on. 'auto' picks top/bottom based on viewport room. Defaults to 'top'. */
	labelPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'auto'
	/** Gap in px between the bird's edge and the label's edge. Defaults to 6. */
	labelGap?: number
}

/** Resolved (non-'auto') label placement, used once the side is picked in showLabel(). */
type ResolvedLabelPlacement = 'top' | 'bottom' | 'left' | 'right'

/** The point and half-extents a caption stands clear of. */
type LabelAnchor = Pick<Destination, 'x' | 'y' | 'halfWidth' | 'halfHeight'>

interface Destination {
	waveSurface?: HTMLElement
	x: number
	y: number
	/**
	 * Half the waypoint element's own box, re-read beside its centre, so a caption can stand clear
	 * of the whole control instead of off the point in the middle of it — which on a row is under
	 * the row's own title. A destination with no element carries the bird's radius instead: with no
	 * box in the way, the bird is the only thing the caption has to clear.
	 */
	halfWidth: number
	halfHeight: number
	element?: HTMLElement // The actual element for theme discovery
	duration: number // How long to pause here
	label?: string // Floating text caption shown near the bird while hovering here
	tone: WaypointTone // Which of tapWave's two inks this waypoint's mark is played back in
}

interface AnimationContext {
	// State machine
	state: AnimationState
	// Position tracking
	cx: number
	cy: number
	// Smoothed toward the current flight leg's heading; persists into hover
	headingDeg: number
	// Flight parameters
	flightStartX: number
	flightStartY: number
	entering: boolean
	phaseStart: number
	destIdx: number
	// Destinations
	destinations: Destination[]
	// Black hole position
	bx: number
	by: number
}

// Shared keyframes - inject once
let keyframesInjected = false
function injectKeyframes(): void {
	if (keyframesInjected) return
	keyframesInjected = true

	const style = document.createElement('style')
	style.id = 'hb-keyframes'
	style.textContent = `
		@keyframes hb-wing-beat {
			0% { transform: scaleY(0.82); opacity: 0.1; }
			100% { transform: scaleY(1.18); opacity: 0.2; }
		}
		@keyframes hb-snitch-wing-beat {
			0% { transform: scaleY(0.82); opacity: 0.55; }
			100% { transform: scaleY(1.18); opacity: 0.8; }
		}
		.schmancy-hb-bird > svg {
			scale: var(--hb-size, 1);
		}
		.schmancy-hb-wing {
			transform-box: fill-box;
			transform-origin: center;
			animation: hb-wing-beat 130ms ease-in-out infinite alternate;
		}
		.schmancy-hb-snitch-wing {
			animation-name: hb-snitch-wing-beat;
		}
		.schmancy-hb-wing--rear {
			animation-delay: 65ms;
		}
		.schmancy-hb-press:focus-visible {
			outline: 2px solid var(--schmancy-sys-color-primary-default);
			outline-offset: 2px;
		}
		.schmancy-hb-bird[role='button'] {
			touch-action: manipulation;
			border-radius: var(--schmancy-radius-circle, 50%);
			outline: none;
		}
		/* The reach of a press: part of the bird to a hit test, and nothing to the eye. */
		.schmancy-hb-bird[role='button']::before {
			content: '';
			position: absolute;
			left: 50%;
			top: 50%;
			width: var(--hb-press);
			height: var(--hb-press);
			transform: translate(-50%, -50%);
		}
		.schmancy-hb-bird[role='button']:focus-visible {
			outline: 2px solid var(--schmancy-sys-color-primary-default, currentColor);
			outline-offset: 4px;
		}
		@keyframes hb-spark-shimmer {
			0%, 100% { opacity: 0.22; filter: brightness(1); }
			50% { opacity: 0.6; filter: brightness(1.35); }
		}
		.schmancy-hb-spark {
			animation: hb-spark-shimmer 2400ms ease-in-out infinite;
		}
		@keyframes hb-disk-inner {
			to { transform: translate(-50%, -50%) rotateX(75deg) rotate(360deg); }
		}
		@keyframes hb-disk-outer {
			to { transform: translate(-50%, -50%) rotateX(70deg) rotate(-360deg); }
		}
		@keyframes hb-disk-mid {
			to { transform: translate(-50%, -50%) rotateX(72deg) rotate(180deg); }
		}
		@keyframes hb-bh-in {
			0% { transform: translate(-50%, -50%) scale(0); }
			60% { transform: translate(-50%, -50%) scale(1.1); }
			100% { transform: translate(-50%, -50%) scale(1); }
		}
		@keyframes hb-horizon-pulse {
			0%, 100% { box-shadow: 0 0 20px 6px var(--hb-color), 0 0 40px 12px var(--hb-color-dim); }
			50% { box-shadow: 0 0 30px 10px var(--hb-color), 0 0 60px 20px var(--hb-color-dim); }
		}
		@keyframes hb-lensing {
			0% { transform: translate(-50%, -50%) rotateX(85deg) scale(1); opacity: 0.6; }
			50% { transform: translate(-50%, -50%) rotateX(85deg) scale(1.05); opacity: 0.8; }
			100% { transform: translate(-50%, -50%) rotateX(85deg) scale(1); opacity: 0.6; }
		}
		@keyframes hb-particle-orbit {
			0% { transform: rotate(0deg) translateX(var(--orbit-radius)) rotate(0deg) scale(1); opacity: 0.9; }
			100% { transform: rotate(720deg) translateX(0px) rotate(-720deg) scale(0); opacity: 0; }
		}
		@keyframes hb-ripple {
			0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.8; }
			100% { transform: translate(-50%, -50%) scale(3); opacity: 0; }
		}
		@media (prefers-reduced-motion: reduce) {
			.schmancy-hb-fx-loop {
				animation: none !important;
			}
			.schmancy-hb-wing {
				animation: none !important;
				opacity: 0.15;
				transform: scaleY(1);
			}
			.schmancy-hb-snitch-wing {
				opacity: 0.7;
			}
			.schmancy-hb-spark {
				animation: none !important;
				opacity: 0.35;
			}
		}
	`
	document.head.appendChild(style)
}

/**
 * Cosmic nebula color palette - based on real emission spectra
 * H-alpha (656nm) - Crimson ionized hydrogen
 * OIII (501nm) - Teal ionized oxygen
 * SII (672nm) - Deep burgundy sulfur
 * Reflection blue - scattered starlight
 */
const COSMIC_COLORS = {
	// H-alpha crimson
	hAlpha: 'rgba(200, 50, 100, 0.85)',
	hAlphaDim: 'rgba(150, 30, 80, 0.5)',
	// OIII teal
	oiii: 'rgba(40, 180, 180, 0.7)',
	oiiiDim: 'rgba(30, 140, 150, 0.35)',
	// Reflection/stellar
	stellar: 'rgba(255, 230, 240, 0.9)',
	stellarDim: 'rgba(255, 180, 200, 0.4)',
	// Deep space
	deepPurple: 'rgba(80, 40, 120, 0.6)',
	deepBlue: 'rgba(20, 40, 100, 0.5)',
	// Event horizon
	voidBlack: 'rgba(0, 0, 0, 1)',
	singularity: 'rgba(10, 5, 20, 1)',
}

/**
 * The default mote's halo/core/centre sit at dead-center so they're rotation-invariant; the two trailing
 * arcs sit off-center on the local -x side (opposite heading 0's +x "forward") so a
 * continuous `rotate()` always keeps them trailing, at any heading, with no flip/symmetry
 * workaround — an orb has no "upside down" to avoid.
 */
const createBirdSVG = (color: string, appearance: HummingbirdOptions['appearance'], host: HTMLElement | null) => {
	if (appearance === 'snitch' || appearance === 'minimal') {
		const palette = getComputedStyle(host ?? document.documentElement)
		const gold = palette.getPropertyValue('--schmancy-sys-color-warning-default').trim() || color
		const light = palette.getPropertyValue('--schmancy-sys-color-warning-container').trim() || gold
		const shade = palette.getPropertyValue('--schmancy-sys-color-warning-pinned').trim() || gold
		if (appearance === 'minimal')
			return `<svg viewBox="0 0 24 24" width="24" height="24" overflow="visible">
			<defs>
				<radialGradient id="hb-minimal-gold" cx="30%" cy="24%" r="78%"><stop offset="0" stop-color="${light}"/><stop offset="0.48" stop-color="${gold}"/><stop offset="1" stop-color="${shade}"/></radialGradient>
				<clipPath id="hb-minimal-body"><circle cx="12" cy="12" r="4.3"/></clipPath>
			</defs>
			<g class="schmancy-hb-wing" style="opacity:0.5"><path d="M8.9 11C6.4 8.4 3.8 7.8 1.5 8.5C3.3 10.5 5.8 11.8 9.2 12.1Z" fill="${color}" fill-opacity="0.35" stroke="${color}" stroke-width="0.4"/></g>
			<g class="schmancy-hb-wing schmancy-hb-wing--rear" style="opacity:0.5"><path d="M15.1 11C17.6 8.4 20.2 7.8 22.5 8.5C20.7 10.5 18.2 11.8 14.8 12.1Z" fill="${color}" fill-opacity="0.35" stroke="${color}" stroke-width="0.4"/></g>
			<circle data-hb-body cx="12" cy="12" r="4.3" fill="url(#hb-minimal-gold)"/>
			<g clip-path="url(#hb-minimal-body)">
				<path d="M10.03 8.33C13.25 9.67 14.24 13.61 11.1 16.03M8.06 11.82Q12 10.93 15.94 12.54" fill="none" stroke="${shade}" stroke-width="0.4" opacity="0.6"/>
				<ellipse cx="10.66" cy="10.12" rx="0.98" ry="0.67" fill="${light}"/>
			</g>
		</svg>`
		return `<svg viewBox="0 0 24 24" width="40" height="40" overflow="visible" style="position:absolute;left:-8px;top:-8px">
			<defs>
				<radialGradient id="hb-snitch-gold" cx="30%" cy="24%" r="78%">
					<stop offset="0" stop-color="${light}"/>
					<stop offset="0.48" stop-color="${gold}"/>
					<stop offset="1" stop-color="${shade}"/>
				</radialGradient>
				<linearGradient id="hb-snitch-wing" x1="0" y1="0" x2="1" y2="1">
					<stop stop-color="${light}" stop-opacity="0.9"/>
					<stop offset="1" stop-color="${gold}" stop-opacity="0.3"/>
				</linearGradient>
			</defs>
			<g class="schmancy-hb-wing schmancy-hb-snitch-wing" style="animation-duration:180ms">
				<path d="M9 10.4C6.9 7.2 3.8 5.8 0.8 6.2C2.6 9.2 5.7 10.8 9.3 11.8Z" fill="url(#hb-snitch-wing)" stroke="${gold}" stroke-width="0.35"/>
				<path d="M2.6 6.9Q5.8 8.6 8.6 10.9M4.8 7Q6.3 9 8.9 11.1" fill="none" stroke="${light}" stroke-width="0.3" opacity="0.7"/>
			</g>
			<g class="schmancy-hb-wing schmancy-hb-wing--rear schmancy-hb-snitch-wing" style="animation-duration:180ms">
				<path d="M15 10.4C17.1 7.2 20.2 5.8 23.2 6.2C21.4 9.2 18.3 10.8 14.7 11.8Z" fill="url(#hb-snitch-wing)" stroke="${gold}" stroke-width="0.35"/>
				<path d="M21.4 6.9Q18.2 8.6 15.4 10.9M19.2 7Q17.7 9 15.1 11.1" fill="none" stroke="${light}" stroke-width="0.3" opacity="0.7"/>
			</g>
			<circle cx="12" cy="12" r="4.8" fill="url(#hb-snitch-gold)" stroke="${shade}" stroke-width="0.35"/>
			<path d="M9.8 7.9C13.4 9.4 14.5 13.8 11 16.5M7.6 11.8Q12 10.8 16.4 12.6" fill="none" stroke="${shade}" stroke-width="0.45" opacity="0.6"/>
			<ellipse class="schmancy-hb-spark" cx="10.5" cy="9.9" rx="1.1" ry="0.75" fill="${light}"/>
		</svg>`
	}
	return `<svg viewBox="0 0 24 24" width="24" height="24" overflow="visible">
	<defs>
		<radialGradient id="hb-glow">
			<stop offset="0%" stop-color="${color}" stop-opacity="0.5"/>
			<stop offset="60%" stop-color="${color}" stop-opacity="0.15"/>
			<stop offset="100%" stop-color="${color}" stop-opacity="0"/>
		</radialGradient>
	</defs>

	<!-- Trailing arcs: pure motion hint behind the mote, no anatomy — faint, blurred, and
	     lighter in mass than the halo so they read as "something alive" rather than a second shape. -->
	<path class="schmancy-hb-wing" d="M 9,6 Q 2,7 2,11" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" opacity="0.15" style="filter:blur(0.8px)"/>
	<path class="schmancy-hb-wing schmancy-hb-wing--rear" d="M 9,18 Q 2,17 2,13" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" opacity="0.15" style="filter:blur(0.8px)"/>

	<!-- Halo -->
	<circle cx="12" cy="12" r="10.5" fill="url(#hb-glow)"/>

	<!-- Solid colour core -->
	<circle cx="12" cy="12" r="4.5" fill="${color}"/>

	<!-- Bright centre — the shimmer lands here now; there's no throat patch left to carry it -->
	<circle class="schmancy-hb-spark" cx="12" cy="12" r="1.6" fill="white"/>
</svg>`
}

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16)
	const g = parseInt(hex.slice(3, 5), 16)
	const b = parseInt(hex.slice(5, 7), 16)
	return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

class HummingbirdDirective extends AsyncDirective {
	private state$ = new BehaviorSubject<AnimationState>('idle')
	private destroyed$ = new BehaviorSubject<boolean>(false)
	private visible$ = new BehaviorSubject<boolean>(true)
	private bird: HTMLElement | null = null
	/** Top-layer host for the bird and every effect it spawns — promoted via the Popover API so no ancestor stacking context (opacity, transform, contain, filter) can trap it beneath later-painting content. */
	private flyingLayer: HTMLElement | null = null
	private blackHole: HTMLElement | null = null
	private particles: HTMLElement[] = []
	private trailCanvas: HTMLCanvasElement | null = null
	private trailCtx: CanvasRenderingContext2D | null = null
	private lastTrailPos: { x: number; y: number } | null = null
	private rafId = 0
	/** `performance.now()` at the frame the visibility gate stopped the loop; 0 while it runs. */
	private pausedAt = 0
	private context: AnimationContext | null = null
	private element: HTMLElement | null = null
	private options: HummingbirdOptions = {}
	private retarget$ = new Subject<HummingbirdOptions | null>()
	private hasHome = false
	/** Configured label placement/gap, read from options in update(). */
	private labelPlacement: HummingbirdOptions['labelPlacement'] = 'top'
	private labelGap = 6
	/** Configured forward flight speed (px/sec), read from options in update(). */
	private speed = FORWARD_SPEED_PX_S
	/** 'auto' resolves once per showLabel() call to 'top' or 'bottom'; this is what positionLabel() reads. */
	private resolvedLabelPlacement: ResolvedLabelPlacement = 'top'
	/** Guards initStateMachine() to run exactly once per play session, even if update() re-fires before the bird exists (e.g. late-resolved waypoints). */
	private initialized = false
	/** Captured once in createBird(). Gates the flourish motions only (wing blur, hover figure-8/jitter, luminance breathing) — heading and the underlying translate stay, since those carry position information rather than decoration. */
	private reducedMotion = false

	/** The last render to reach this part while it was off the page, played when it returns. */
	private heldBack: { part: ElementPart; options: boolean | HummingbirdOptions | undefined } | null = null
	/** The one stream a `stand` flight attends, carrying the line it was last given. */
	private standing$ = new ReplaySubject<HummingbirdAttendStatus>(1)
	/** The status stream `attend` names; switching it unsubscribes the last one, and null (disconnect, attend dropped) leaves none. */
	private attendSource$ = new BehaviorSubject<Observable<HummingbirdAttendStatus> | null>(null)
	/** The latest attend status. Outlives a session: a `working` after the bird has left brings it back. */
	private attendStatus: HummingbirdAttendStatus | null = null
	/** Arc length along the attend loop the bird is at — advanced while working, held while it reports. */
	private attendStep = 0
	private attendFrame = 0
	/** Where the current attend leg started, relative to the surface's top-left, so a scroll mid-leg moves both ends. */
	private attendFrom = { x: 0, y: 0 }
	private attendLegMs = 0
	/** The leg reached the loop and the status's label (if any) is armed — the latch for the arrival block. */
	private attendArrived = false
	/** The bird's departure, a done bird's or a stopped one's, kept so new work can call it back mid-exit. */
	private attendExit: Animation | null = null
	/** The viewport point the next bird to be created appears at, from the `working` that sent it. */
	private attendStart: { x: number; y: number } | null = null
	/** The bird is growing at its start point; its leg onto the loop begins once it is whole. */
	private attendLaunching = false
	/** The current leg's length, which caps the snitch's lift so a short hop is not a 65px climb. */
	private attendLegPx = 0
	/** The `on` flight's drift over the surface; held while the bird reports, so it reports where it was. */
	private wander: Wander = {
		x: WANDER_HARMONICS.map(() => Math.random() * 2 * Math.PI),
		y: WANDER_HARMONICS.map(() => Math.random() * 2 * Math.PI),
		driftX: Math.random() * 2 * Math.PI,
		driftY: Math.random() * 2 * Math.PI,
		rate: 1,
	}
	/** What a press on the bird does right now. */
	private pressState: PressState = 'pass'
	/** The bird a press is wired to, and what lets go of it. */
	private pressedBird: HTMLElement | null = null
	private pressWiring: AbortController | null = null
	/** When the page last scrolled or resized under the bird. */
	private pageMovedAt = Number.NEGATIVE_INFINITY
	/** The bird's path point last frame, before the hover figure-8 — what its heading turns toward. */
	private attendPath = { x: 0, y: 0 }
	/** The working line's words, kept once their time has run so a repeat of them is no news. */
	private workingLine = ''
	/** How long the working line has stood, counted in the flight's frames so time out of sight is not spent. */
	private workingLineMs = 0

	constructor(partInfo: PartInfo) {
		super(partInfo)
		this.attendSource$
			.pipe(
				distinctUntilChanged(),
				switchMap(status$ => status$ ?? EMPTY),
			)
			.subscribe(status => this.attend(status))
	}

	render(options?: boolean | HummingbirdOptions) {
		void options
		return noChange
	}

	override update(part: ElementPart, [options = true]: [boolean | HummingbirdOptions | undefined]) {
		// A render can reach a part that has left the page, and no disconnected() is coming to take down what it starts.
		if (!this.isConnected) {
			this.heldBack = { part, options }
			return noChange
		}
		const element = part.element as HTMLElement
		const given: HummingbirdOptions = typeof options === 'object' ? options : {}
		if (given.stand) this.standing$.next({ kind: 'working', start: given.stand.at, label: given.stand.label })
		const opts: HummingbirdOptions = given.stand
			? { ...given, attend: { around: given.stand.at, flight: 'on', status: this.standing$ } }
			: given
		const shouldPlay = typeof options === 'boolean' ? options : opts.playing !== false

		if (!shouldPlay) {
			this.options = { ...opts, playing: false }
			this.retarget$.next(null)
			this.attendSource$.next(opts.attend?.status ?? null)
			this.fadeOutAndCleanup()
			return noChange
		}

		const previous = this.options
		this.element = element
		this.options = opts
		this.labelPlacement = opts.labelPlacement ?? 'top'
		this.labelGap = opts.labelGap ?? 6
		this.speed = opts.speed ?? FORWARD_SPEED_PX_S

		if (this.bird && (previous.appearance !== opts.appearance || previous.motion !== opts.motion))
			this.bird.innerHTML = createBirdSVG(this.currentColor, opts.appearance, this.element)
		if (this.bird && previous.size !== opts.size) this.sizeBird(this.bird)

		// An attend flight takes off on its status, not on the render: nothing is being worked on until
		// the first `working` (or `failed`) arrives. A status already standing when `playing` comes
		// back is that arrival.
		this.attendSource$.next(opts.attend?.status ?? null)
		if (opts.attend) {
			if (!this.initialized && this.attendStatus && this.attendStatus.kind !== 'done') {
				this.launchFrom(this.attendStatus)
				this.takeOff()
			}
			// Stopped and started again inside its own exit, with work still standing: the bird never left.
			else if (this.attendExit && this.attendStatus && this.attendStatus.kind !== 'done') this.resumeAttend()
			// A bird still growing reads the new surface when its first leg begins; one leaving is done with it.
			else if (
				this.state$.value === 'attending' &&
				!this.attendLaunching &&
				(!samePlace(previous.attend?.around, opts.attend.around) || previous.attend?.flight !== opts.attend.flight)
			)
				this.beginAttendLeg(performance.now())
			return noChange
		}

		if (this.bird) {
			const before = previous.waypoints ?? []
			const after = opts.waypoints ?? []
			const changed =
				previous.stationary !== opts.stationary ||
				previous.home !== opts.home ||
				before.length !== after.length ||
				after.some((waypoint, index) => {
					const old = before[index]
					if (waypoint === old) return false
					if (
						typeof waypoint === 'string' ||
						waypoint instanceof HTMLElement ||
						typeof old === 'string' ||
						old instanceof HTMLElement ||
						!old
					)
						return true
					return (
						waypoint.ref !== old.ref ||
						waypoint.waveSurface !== old.waveSurface ||
						waypoint.duration !== old.duration ||
						waypoint.label !== old.label ||
						waypoint.tone !== old.tone
					)
				})
			if (changed && this.context && ['flying', 'hovering'].includes(this.state$.value)) this.retarget$.next(opts)
			return noChange
		}

		if (this.initialized) return noChange // State machine already running; options refreshed above for createBird() to read later
		this.takeOff()
		return noChange
	}

	private takeOff(): void {
		this.initialized = true

		// Reset destroyed state so subscriptions work again on a subsequent play
		if (this.destroyed$.value) {
			this.destroyed$.next(false)
			this.state$.next('idle')
		}

		// A same-tree z-index only wins within its own stacking context, and any ancestor
		// (opacity, transform, filter, contain, isolation — schmancy-area's :host among them)
		// traps the bird beneath later-painting content; the top layer is the one rendering
		// domain no ancestor stacking context can reach.
		const layer = document.createElement('div')
		layer.popover = 'manual'
		layer.toggleAttribute('data-schmancy-hummingbird', true)
		layer.style.cssText = `
			position: fixed;
			inset: 0;
			margin: 0;
			border: none;
			padding: 0;
			max-width: none;
			max-height: none;
			width: 100%;
			height: 100%;
			background: transparent;
			pointer-events: none;
		`
		document.body.appendChild(layer)
		layer.showPopover()
		this.flyingLayer = layer

		injectKeyframes()
		this.initStateMachine()
	}

	/**
	 * A status arriving. A repeat of the standing one changes nothing — a consumer whose stream
	 * replays on resubscription must not restart the bird's report. Otherwise the bird takes off if
	 * there is work to attend and it is gone, is called back if it is mid-departure, or starts a new
	 * leg from wherever it is. A new line on work already in hand changes the words, never the flight:
	 * tickAttending() reconciles the pill with it on the next frame.
	 */
	private attend(status: HummingbirdAttendStatus): void {
		this.launchFrom(status)
		const standing = this.attendStatus
		if (standing && standing.kind === status.kind && (standing.label ?? '') === (status.label ?? '')) return
		this.attendStatus = status
		if (this.options.playing === false) return
		if (!this.initialized) {
			if (status.kind !== 'done') this.takeOff()
			return
		}
		// Still in its lead-in, createBird() reads the status it lands on; still growing, the leg that
		// begins once it is whole does.
		if (!this.context || !this.bird || this.attendLaunching) return
		if (this.attendExit && status.kind === 'done') return
		if (standing?.kind === 'working' && status.kind === 'working' && !this.attendExit) return
		this.resumeAttend()
	}

	/** Where a bird that does not exist yet will grow, read when the work arrives, or when `playing` returns to it. */
	private launchFrom(status: HummingbirdAttendStatus): void {
		if (this.bird || status.kind !== 'working' || !status.start) return
		const r = status.start instanceof Element ? status.start.getBoundingClientRect() : status.start
		this.attendStart = { x: r.x + (r.width ?? 0) / 2, y: r.y + (r.height ?? 0) / 2 }
	}

	/** Takes the bird on from wherever it is, calling it back if it was leaving. */
	private resumeAttend(): void {
		if (this.attendExit) {
			this.attendExit.cancel()
			this.attendExit = null
			for (const fade of this.trailCanvas?.getAnimations() ?? []) fade.cancel()
		}
		this.state$.next('attending')
		this.beginAttendLeg(performance.now())
	}

	private initStateMachine(): void {
		this.retarget$
			.pipe(
				switchMap(options => {
					if (!options || !this.context) return EMPTY
					const context = this.context
					return forkJoin([
						from(this.discoverWaypoints(options.waypoints ?? [])),
						options.home ? from(this.getPosition(options.home)) : of(null),
					]).pipe(
						filter(([waypoints, home]) => !!this.bird && this.context === context && !!(waypoints.length || home)),
						tap(([waypoints, home]) => {
							this.hideLabel()
							this.hideWaveClip()
							this.armedDestIdx = -1
							context.flightStartX = context.cx
							context.flightStartY =
								context.cy -
								(options.motion === 'snitch' && !this.reducedMotion ? Math.sin(performance.now() / 520) * 4 : 0)
							context.entering = false
							context.destinations = home ? [...waypoints, home] : waypoints
							context.destIdx = 0
							context.phaseStart = performance.now()
							this.hasHome = !!home
							this.pausedAt = this.visible$.value ? 0 : context.phaseStart
							this.state$.next('flying')
							this.startAnimationLoop()
						}),
						catchError(() => EMPTY),
					)
				}),
				takeUntil(this.destroyed$.pipe(filter(v => v))),
			)
			.subscribe()

		this.state$
			.pipe(
				distinctUntilChanged(),
				filter(() => !this.destroyed$.value),
				switchMap(state => this.handleStateTransition(state)),
				takeUntil(this.destroyed$.pipe(filter(v => v))),
			)
			.subscribe()

		// All three must be true: IntersectionObserver + Page Visibility + Parent Playing State
		combineLatest([
			new Observable<boolean>(subscriber => {
				if (!this.element || typeof IntersectionObserver === 'undefined') {
					subscriber.next(true)
					return
				}
				const observer = new IntersectionObserver(entries => subscriber.next(entries[0].isIntersecting), {
					threshold: 0,
				})
				observer.observe(this.element)
				return () => observer.disconnect()
			}),
			fromEvent(document, 'visibilitychange').pipe(
				map(() => document.visibilityState === 'visible'),
				startWith(document.visibilityState === 'visible'),
			),
			new Observable<boolean>(subscriber => {
				if (!this.element) {
					subscriber.next(true)
					return
				}
				// Find ancestor with a 'playing' property (scene components)
				let ancestor: HTMLElement | null = this.element
				let playingHost: HTMLElement | null = null
				while (ancestor) {
					if ('playing' in ancestor) {
						playingHost = ancestor
						break
					}
					ancestor = ancestor.parentElement
				}
				if (!playingHost) {
					subscriber.next(true)
					return
				}
				subscriber.next((playingHost as HTMLElement & { playing: boolean }).playing)
				const observer = new MutationObserver(() => {
					subscriber.next((playingHost as HTMLElement & { playing: boolean }).playing)
				})
				observer.observe(playingHost, { attributes: true, attributeFilter: ['playing'] })
				return () => observer.disconnect()
			}),
		])
			.pipe(
				map(([inView, tabActive, playing]) => inView && tabActive && playing),
				distinctUntilChanged(),
				// The top layer does not scroll with the document, so stopping the loop alone leaves the
				// bird frozen over whatever scrolled into its last viewport coordinate. Above the context
				// filter, because the gate can go false before there is a context.
				tap(visible => {
					if (this.flyingLayer) this.flyingLayer.style.visibility = visible ? 'visible' : 'hidden'
					this.visible$.next(visible)
				}),
				filter(() => !this.destroyed$.value && !!this.context),
				takeUntil(this.destroyed$.pipe(filter(v => v))),
			)
			.subscribe(visible => {
				if (visible && !this.rafId) {
					// `phaseStart` measures time the reader was actually present for, never wall clock: every
					// phase takes its elapsed off it — the leg's progress, the hover's reading budget, the
					// spiral — so the gap spent off-screen is handed back before the loop resumes and the
					// flight continues where it paused instead of where the clock went.
					if (this.pausedAt && this.context) this.context.phaseStart += performance.now() - this.pausedAt
					this.pausedAt = 0
					this.startAnimationLoop()
				} else if (!visible && this.rafId) {
					this.pausedAt = performance.now()
					cancelAnimationFrame(this.rafId)
					this.rafId = 0
				}
			})

		// Wait for delay + host visibility, then discover elements and create bird
		const delay = this.options.delay ?? (this.options.attend ? 0 : 900)
		timer(delay)
			.pipe(
				filter(() => !this.destroyed$.value && !this.bird),
				switchMap(
					() =>
						new Observable<void>(subscriber => {
							if (!this.element) {
								subscriber.next()
								subscriber.complete()
								return
							}
							const checkVisibility = () => {
								if (this.isElementVisible(this.element!)) {
									subscriber.next()
									subscriber.complete()
								} else {
									timer(50)
										.pipe(takeUntil(this.destroyed$.pipe(filter(v => v))))
										.subscribe(checkVisibility)
								}
							}
							checkVisibility()
						}),
				),
				switchMap(() => this.createBird()),
				tap(() => {
					if (!this.destroyed$.value && this.options.playing !== false) {
						this.state$.next(this.options.attend ? 'attending' : this.options.stationary ? 'hovering' : 'flying')
					}
				}),
				takeUntil(this.destroyed$.pipe(filter(v => v))),
			)
			.subscribe()
	}

	private handleStateTransition(state: AnimationState) {
		switch (state) {
			case 'idle':
				return EMPTY
			case 'flying':
			case 'hovering':
			case 'spiraling':
			case 'absorbed':
			case 'attending':
				this.startAnimationLoop()
				return EMPTY
			case 'done':
				this.cleanup()
				// Emit completion event so parent can react
				this.element?.dispatchEvent(new CustomEvent('hummingbird-complete', { bubbles: true, composed: true }))
				return EMPTY
			default:
				return EMPTY
		}
	}

	private async createBird(): Promise<void> {
		if (!this.element || !this.flyingLayer) return

		const bird = document.createElement('div')
		bird.innerHTML = createBirdSVG(this.currentColor, this.options.appearance, this.element)
		bird.className = 'pointer-events-none schmancy-hb-bird'
		bird.style.cssText = `
			position: absolute;
			left: 0;
			top: 0;
			width: 24px;
			height: 24px;
			z-index: 99999;
			will-change: transform, opacity, filter;
			transform-origin: center center;
		`
		this.sizeBird(bird)
		this.flyingLayer.appendChild(bird)
		this.bird = bird
		this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

		this.discoverInitialColor()

		const rect = this.element.getBoundingClientRect()
		const start = this.entryPoint()
		if (!start) return

		if (this.options.attend) {
			const launch = this.attendStart
			this.attendStart = null
			const at = launch ?? start
			this.context = {
				state: 'attending',
				cx: at.x,
				cy: at.y,
				headingDeg: 0,
				flightStartX: at.x,
				flightStartY: at.y,
				entering: true,
				phaseStart: performance.now(),
				destIdx: 0,
				destinations: [],
				bx: 0,
				by: 0,
			}
			this.attendPath = { x: at.x, y: at.y }
			this.beginAttendLeg(this.context.phaseStart)
			const surface = boxOf(this.options.attend.around ?? this.element)
			if (this.options.attend.flight === 'on' && this.reducedMotion) {
				// Reduced motion on the surface is a bird sitting at its centre from its first frame.
				const seen = inView(surface)
				this.context.cx = seen.left + seen.width / 2
				this.context.cy = seen.top + seen.height / 2
			} else if (launch && this.reducedMotion) {
				// Reduced motion keeps no flight to watch: the bird is on the loop from its first frame.
				this.attendLegMs = 0
				const point = loopPoint(surface, this.attendStep)
				this.context.cx = point.x
				this.context.cy = point.y
			}
			this.attendPath = { x: this.context.cx, y: this.context.cy }
			this.attendLaunching = !!launch && !this.reducedMotion
			this.bird.style.transform = `translate(${this.context.cx - BIRD_RADIUS}px, ${this.context.cy - BIRD_RADIUS}px) scale(${this.attendLaunching ? 0 : 1})`
			return
		}

		let configured = this.options
		let waypoints = await this.discoverWaypoints(configured.waypoints || [])
		let home = configured.home ? await this.getPosition(configured.home) : null
		while (configured !== this.options && !this.destroyed$.value && this.options.playing !== false) {
			configured = this.options
			waypoints = await this.discoverWaypoints(configured.waypoints || [])
			home = configured.home ? await this.getPosition(configured.home) : null
		}
		if (!this.bird || this.destroyed$.value || this.options.playing === false) return

		const destinations = [...waypoints]
		this.hasHome = !!home
		if (home) destinations.push(home)

		// If no destinations, create simple fly-through
		if (destinations.length === 0) {
			destinations.push({
				x: rect.left + rect.width + HOST_CLEARANCE_PX,
				y: rect.top + rect.height * 0.5,
				halfWidth: BIRD_RADIUS,
				halfHeight: BIRD_RADIUS,
				duration: PAUSE_DURATION_MS,
				tone: 'add',
			})
		}

		const origin = this.options.stationary ? destinations[0] : start
		this.context = {
			state: this.options.stationary ? 'hovering' : 'flying',
			cx: origin.x,
			cy: origin.y,
			headingDeg: 0,
			flightStartX: start.x,
			flightStartY: start.y,
			entering: true,
			phaseStart: performance.now(),
			destIdx: 0,
			destinations,
			bx: 0,
			by: 0,
		}

		// Offset by half the bird's size so it centers on the position; the first tickFlying()
		// frame overwrites this with the real heading a moment later.
		this.bird.style.transform = `translate(${origin.x - BIRD_RADIUS}px, ${origin.y - BIRD_RADIUS}px)`
	}

	/** Run every frame, as whether a press lands changes with the flight and the host's options may change at any time. */
	private syncPress(now: number): void {
		const bird = this.bird
		const press = this.options.press
		if (!bird) return
		if (!press) {
			if (this.pressedBird) this.releasePress()
			return
		}
		if (this.pressedBird !== bird) this.wirePress(bird)
		const state = this.state$.value
		const leaving = this.attendExit !== null || this.options.playing === false || state === 'spiraling' || state === 'absorbed' || state === 'done'
		const flying = state === 'flying' || (state === 'attending' && (this.attendLaunching || (!this.attendArrived && this.attendLegMs > 0)))
		this.pressState = press.refuse ?? (leaving ? 'pass' : flying ? 'fly' : now - this.pageMovedAt < PAGE_SETTLES_MS ? 'swallow' : 'take')
		const flat = this.pressState === 'pass' || this.pressState === 'fly'
		bird.style.pointerEvents = flat ? '' : 'auto'
		bird.style.cursor = this.pressState === 'take' ? 'pointer' : ''
		if (this.pressState === 'pass') bird.removeAttribute('tabindex')
		else bird.tabIndex = 0
		bird.setAttribute('aria-label', press.label)
		bird.title = press.label
		const aria = { 'aria-haspopup': press.haspopup, 'aria-expanded': press.expanded === undefined ? undefined : String(press.expanded), 'aria-keyshortcuts': press.keyshortcuts }
		for (const [name, value] of Object.entries(aria)) {
			if (value === undefined) bird.removeAttribute(name)
			else bird.setAttribute(name, value)
		}
	}

	private wirePress(bird: HTMLElement): void {
		this.pressWiring?.abort()
		const wiring = new AbortController()
		this.pressWiring = wiring
		this.pressedBird = bird
		bird.setAttribute('role', 'button')
		// It flies on its own, and what it opens does not swim after it.
		bird.toggleAttribute('data-overlay-anchor-still', true)
		bird.style.setProperty('--hb-press', `${window.matchMedia('(any-pointer: coarse)').matches ? TOUCH_PRESS_PX : MOUSE_PRESS_PX}px`)
		const options = { signal: wiring.signal }
		// The page under the bird never hears a press on it.
		for (const type of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend']) bird.addEventListener(type, event => event.stopPropagation(), options)
		bird.addEventListener(
			'click',
			event => {
				event.stopPropagation()
				if (this.pressState === 'take') this.options.press?.onPress()
			},
			options,
		)
		bird.addEventListener(
			'keydown',
			event => {
				if (event.key !== 'Enter' && event.key !== ' ') return
				event.preventDefault()
				event.stopPropagation()
				// A key is aimed at the bird, wherever it is flying.
				if (!event.repeat && (this.pressState === 'take' || this.pressState === 'fly')) this.options.press?.onPress()
			},
			options,
		)
		const moved = () => (this.pageMovedAt = performance.now())
		document.addEventListener('scroll', moved, { ...options, capture: true, passive: true })
		window.addEventListener('resize', moved, options)
	}

	private releasePress(): void {
		const bird = this.pressedBird
		this.pressWiring?.abort()
		this.pressWiring = null
		this.pressedBird = null
		this.pressState = 'pass'
		if (!bird) return
		for (const name of ['role', 'data-overlay-anchor-still', 'tabindex', 'title', 'aria-label', 'aria-haspopup', 'aria-expanded', 'aria-keyshortcuts']) bird.removeAttribute(name)
		bird.style.pointerEvents = ''
		bird.style.cursor = ''
	}

	private sizeBird(bird: HTMLElement): void {
		if (this.options.size) bird.style.setProperty('--hb-size', String(this.options.size / (BIRD_RADIUS * 2)))
		else bird.style.removeProperty('--hb-size')
	}

	/** Current bird color — empty until `discoverInitialColor()` resolves a theme color. */
	private currentColor = ''

	/** Dispatches event FROM the host element so it bubbles up to the nearest theme. */
	private discoverInitialColor(): void {
		if (!this.element) return

		fromEvent<CustomEvent<{ theme: HTMLElement & { color?: string } }>>(window, ThemeHereIAm)
			.pipe(
				take(1),
				takeUntil(timer(150)),
				map(e => e.detail.theme?.color),
				filter((color): color is string => !!color && color !== this.currentColor),
				takeUntil(this.destroyed$.pipe(filter(v => v))),
			)
			.subscribe(color => {
				if (!this.bird) return
				this.currentColor = color
				this.bird.innerHTML = createBirdSVG(color, this.options.appearance, this.element)
			})

		// Dispatch FROM the host element (bubbles up to nearest theme)
		this.element.dispatchEvent(new CustomEvent(ThemeWhereAreYou, { bubbles: true, composed: true }))
	}

	/** Dispatches event FROM the destination element so it bubbles to its nearest theme. */
	private updateBirdColor(): void {
		if (!this.bird || !this.context || this.destroyed$.value) return

		const dest = this.context.destinations[this.context.destIdx]
		if (!dest?.element) return

		fromEvent<CustomEvent<{ theme: HTMLElement & { color?: string } }>>(window, ThemeHereIAm)
			.pipe(
				take(1),
				takeUntil(timer(100)),
				map(e => e.detail.theme?.color),
				filter((color): color is string => !!color && color !== this.currentColor),
				takeUntil(this.destroyed$.pipe(filter(v => v))),
			)
			.subscribe(color => {
				if (!this.bird) return
				this.currentColor = color
				this.bird.innerHTML = createBirdSVG(color, this.options.appearance, this.element)
			})

		// Dispatch FROM the destination element (bubbles up to its nearest theme)
		dest.element.dispatchEvent(new CustomEvent(ThemeWhereAreYou, { bubbles: true, composed: true }))
	}

	/** Accepts both a plain ElementRef and a Waypoint object with a per-stop duration. */
	private discoverWaypoints(waypoints: (ElementRef | Waypoint)[]): Promise<Destination[]> {
		if (waypoints.length === 0) return Promise.resolve([])

		const normalized = waypoints.map(wp => {
			if (typeof wp === 'string' || wp instanceof HTMLElement) {
				return {
					ref: wp,
					waveSurface: undefined,
					duration: PAUSE_DURATION_MS,
					label: undefined as string | undefined,
					tone: 'add' as WaypointTone,
				}
			}
			return {
				ref: wp.ref,
				waveSurface: wp.waveSurface,
				duration: wp.duration ?? PAUSE_DURATION_MS,
				label: wp.label,
				tone: wp.tone ?? 'add',
			}
		})

		const discoveries$ = normalized.map(wp =>
			forkJoin({
				element: from(this.discoverElement(wp.ref)),
				waveSurface: wp.waveSurface ? from(this.discoverElement(wp.waveSurface)) : of(null),
			}),
		)

		return new Promise(resolve => {
			forkJoin(discoveries$)
				.pipe(
					map(elements =>
						elements
							.map(({ element: el, waveSurface }, i): Destination | null => {
								if (!el) return null
								const r = el.getBoundingClientRect()
								return {
									x: r.left + r.width / 2,
									y: r.top + r.height / 2,
									halfWidth: r.width / 2,
									halfHeight: r.height / 2,
									element: el,
									waveSurface: waveSurface ?? el,
									duration: normalized[i].duration,
									label: normalized[i].label,
									tone: normalized[i].tone,
								}
							})
							.filter((d): d is Destination => d !== null),
					),
					takeUntil(this.destroyed$.pipe(filter(v => v))),
				)
				.subscribe({
					next: positions => resolve(positions),
					error: () => resolve([]),
				})
		})
	}

	private discoverElement(ref: ElementRef): Promise<HTMLElement | null> {
		if (ref instanceof HTMLElement) {
			return Promise.resolve(ref)
		}

		return new Promise(resolve => {
			// discover() resolves synchronously now — the 300ms wait it used to
			// take was a discovery timeout, not a mount wait. This waypoint's
			// element is assumed already mounted by the time the bird flies to
			// it (the caller renders waypoints before invoking this directive).
			discover<HTMLElement>(ref)
				.pipe(takeUntil(this.destroyed$.pipe(filter(v => v))))
				.subscribe({
					next: el => resolve(el),
					error: () => resolve(null),
				})
		})
	}

	/** Uses the element's center position — the target point for the black-hole effect. */
	private async getPosition(ref: ElementRef): Promise<Destination | null> {
		const el = await this.discoverElement(ref)
		if (!el) return null
		const r = el.getBoundingClientRect()
		return {
			x: r.left + r.width / 2,
			y: r.top + r.height / 2,
			halfWidth: r.width / 2,
			halfHeight: r.height / 2,
			element: el,
			duration: PAUSE_DURATION_MS,
			tone: 'add',
		}
	}

	/**
	 * The active destination's live viewport box — centre and half-extents, off one rect read —
	 * re-read every frame it is flown to or hovered. A coordinate measured once is wrong from the
	 * moment its target moves, and a target moves for reasons that announce themselves late or not
	 * at all: a scroll (whose
	 * events arrive in bursts, so anything debounced fires only once the user has already
	 * stopped), the directive's own smooth `scrollIntoView`, a card easing into place, a
	 * reflow above it. Read at the top of the tick, before the frame's own writes, so layout
	 * is still clean from the last paint and this costs cached geometry, not a reflow.
	 */
	private measureActiveDestination(ctx: AnimationContext): Destination {
		const dest = ctx.destinations[ctx.destIdx]
		if (!dest.element) return dest
		const r = dest.element.getBoundingClientRect()
		dest.x = r.left + r.width / 2
		dest.y = r.top + r.height / 2
		dest.halfWidth = r.width / 2
		dest.halfHeight = r.height / 2
		return dest
	}

	/** Where a flight enters, before the first waypoint: clear of the host's leading edge, level with its middle. */
	private entryPoint(): { x: number; y: number } | null {
		if (!this.element) return null
		const r = this.element.getBoundingClientRect()
		return { x: r.left - HOST_CLEARANCE_PX, y: r.top + r.height / 2 }
	}

	/**
	 * Carries the whole closing sequence with `home`. Everything from the spiral through the
	 * collapse is spawned at one point, and that point is viewport-space like every other
	 * coordinate here, so a scroll during the last second would otherwise leave the black hole
	 * over the part of the page that has moved on. The bird's own position moves by the same
	 * delta because `tickSpiraling` accumulates it rather than deriving it each frame.
	 */
	private trackBlackHoleToHome(ctx: AnimationContext): void {
		const home = this.measureActiveDestination(ctx)
		if (!home.element) return

		ctx.cx += home.x - ctx.bx
		ctx.cy += home.y - ctx.by
		ctx.bx = home.x
		ctx.by = home.y

		if (!this.blackHoleLayer) return
		const dx = home.x - this.blackHoleOrigin.x
		const dy = home.y - this.blackHoleOrigin.y
		this.blackHoleLayer.style.transform = `translate(${dx}px, ${dy}px)`
	}

	private startAnimationLoop(): void {
		if (this.rafId) return

		// A state transition can reach here while the gate is false — a host already out of view when
		// the delay expires still passes the computed-style check the bird's creation waits on. Frames
		// run under no reader then, and `pausedAt` unset means that time is never handed back to
		// `phaseStart`, so the bird returns further along its flight than it was left. The gate's own
		// subscriber starts the loop when it opens.
		if (!this.visible$.value) {
			if (!this.pausedAt) this.pausedAt = performance.now()
			return
		}

		const tick = (now: number) => {
			// Not gated on the bird: the collapse outlives it and still has a frame's work to do.
			if (!this.context || this.destroyed$.value || !this.visible$.value) {
				this.rafId = 0
				return
			}

			switch (this.state$.value) {
				case 'flying':
					this.tickFlying(now)
					break
				case 'hovering':
					this.tickHovering(now)
					break
				case 'spiraling':
					this.tickSpiraling(now)
					break
				case 'absorbed':
					this.tickAbsorbed(now)
					break
				case 'attending':
					this.tickAttending(now)
					break
			}
			this.syncPress(now)

			this.rafId = requestAnimationFrame(tick)
		}

		this.rafId = requestAnimationFrame(tick)
	}

	private tickFlying(now: number): void {
		if (!this.context || !this.bird) return

		const ctx = this.context
		const elapsed = now - ctx.phaseStart
		const dest = this.measureActiveDestination(ctx)

		// The leg's start is the previous waypoint's own centre — or, for the first leg, the
		// host's entry point — and is re-read for the same reason the destination is: a page
		// scrolling under a flight moves both ends of it, and a leg drawn from a frozen start
		// slides off the page by exactly the distance the page went.
		const from = ctx.destIdx > 0 ? ctx.destinations[ctx.destIdx - 1] : null
		if (from?.element) {
			const r = from.element.getBoundingClientRect()
			from.x = r.left + r.width / 2
			from.y = r.top + r.height / 2
			ctx.flightStartX = from.x
			ctx.flightStartY = from.y
		} else if (ctx.destIdx === 0 && ctx.entering && dest.element) {
			// `dest.element` gates this: with no waypoints at all the destination is a fabricated
			// point off the host that nothing can re-measure, so its leg keeps both ends fixed
			// rather than stretching one against the other.
			const entry = this.entryPoint()
			if (entry) {
				ctx.flightStartX = entry.x
				ctx.flightStartY = entry.y
			}
		}

		const dx = dest.x - ctx.flightStartX
		const dy = dest.y - ctx.flightStartY
		const dist = Math.hypot(dx, dy)
		const duration = this.options.motion === 'snitch' ? SNITCH_LEG_MS : (dist / this.speed) * 1000
		const t = Math.min(1, elapsed / duration)

		// Cubic ease-in-out
		const e =
			this.options.motion === 'snitch' ? 1 - (1 - t) ** 3 : t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

		ctx.cx = ctx.flightStartX + dx * e
		ctx.cy = ctx.flightStartY + dy * e
		if (this.options.motion === 'snitch' && !this.reducedMotion)
			ctx.cy += Math.sin(now / 520) * 4 - Math.sin(t * Math.PI) * SNITCH_LIFT_PX

		// Heading eases toward this leg's (constant) direction rather than snapping at the
		// waypoint transition — see HEADING_SMOOTHING. The mod-360 trick keeps the turn short:
		// without it, 170deg easing toward -170deg would spin the long way round through 0.
		if (dist > 0) {
			const targetHeading = Math.atan2(dy, dx) * (180 / Math.PI)
			const delta = ((targetHeading - ctx.headingDeg + 540) % 360) - 180
			ctx.headingDeg += delta * HEADING_SMOOTHING
		}

		// One shared value drives both the scale and luminance breathe so they read as one
		// "alive" pulse rather than two independent flickers.
		const breathe = this.reducedMotion || this.options.motion === 'snitch' ? 1 : 1 + Math.sin(now * 0.0016) * 0.04
		this.bird.style.transform = `translate(${ctx.cx - BIRD_RADIUS}px, ${ctx.cy - BIRD_RADIUS}px) rotate(${this.options.motion === 'snitch' ? 0 : ctx.headingDeg}deg) scale(${breathe})`
		if (this.options.motion === 'snitch') {
			this.bird.style.filter = ''
			this.flutter(now)
		} else if (!this.reducedMotion) this.bird.style.filter = `brightness(${breathe})`

		// Draws a fading vapor trail as the bird flies
		if (this.options.showConnections) {
			this.drawTrail(ctx.cx, ctx.cy)
			this.fadeTrail()
		}

		// Update color based on section the bird is flying over
		if (Math.floor(elapsed / 200) !== Math.floor((elapsed - 16) / 200)) {
			this.updateBirdColor()
		}

		if (t >= 1) {
			const isLastDestination = ctx.destIdx >= ctx.destinations.length - 1

			if (isLastDestination && this.hasHome) {
				// Has home: transition to spiraling into black hole
				ctx.bx = dest.x
				ctx.by = dest.y
				ctx.phaseStart = now
				this.createBlackHole(ctx.bx, ctx.by)
				this.state$.next('spiraling')
			} else {
				// Transition to hovering (including last waypoint when no home)
				ctx.phaseStart = now
				this.state$.next('hovering')
			}
		}
	}

	/** Clips this waypoint's tap waves to the control's own box; each wave is a child of it. */
	private waveClip: HTMLDivElement | null = null
	/** Which beat of the current hover has already spawned its wave — the latch that keeps one frame's crossing from firing twice. */
	private lastWaveBeat = -1
	/** Active floating text caption for current waypoint */
	private labelEl: HTMLElement | null = null
	private labelAnimation: Animation | null = null
	/** The words the pill was last given — its textContent also holds the outgoing line while a relabel crossfades. */
	private labelText = ''
	/** The pill's size tween while a working line gives way to the next, kept so a quicker next line takes over from it. */
	private labelSwap: Animation | null = null
	/** Index of the waypoint whose hover treatment is already armed — the latch that fires the arming block exactly once per waypoint. */
	private armedDestIdx = -1
	/** How long the armed waypoint holds: its own `duration`, or that floor raised to its caption's reading time. Priced once at arming, not per frame. */
	private hoverDwellMs = 0

	private tickHovering(now: number): void {
		if (!this.context || !this.bird) return

		const ctx = this.context
		const elapsed = now - ctx.phaseStart
		const dest = this.measureActiveDestination(ctx)

		// Arms on whichever frame first enters this waypoint's hover, however late that frame is;
		// the bird stays visible and breathing throughout — these support it, they don't stand in
		// for it. Latched on the waypoint's own index, not on elapsed time and not on the clip's own
		// existence, so a frame budget the device misses cannot delete the coach mark.
		if (this.armedDestIdx !== ctx.destIdx) {
			this.armedDestIdx = ctx.destIdx
			// The waypoint's own duration is the floor; a caption holds the bird until it has been read.
			this.hoverDwellMs = dest.label ? Math.max(dest.duration, captionDwellMs(dest.label)) : dest.duration
			this.lastWaveBeat = -1
			if (dest.element) this.openWaveClip(dest.waveSurface ?? dest.element, dest.tone)
			if (dest.label) this.showLabel(dest.label, dest)
			this.positionLabel(dest)
		}

		// One tap per beat, off the hover's own clock rather than a timer of its own: `elapsed` is
		// already the time the reader was present for, so a hover paused off-screen resumes its
		// cadence where it stopped instead of having tapped at a control nobody was looking at. A
		// beat whose wave cannot finish before the bird leaves never starts — a gesture cut off
		// halfway reads as a glitch, where one fewer tap reads as nothing at all.
		if (this.waveClip && !this.reducedMotion) {
			const beat = Math.floor(elapsed / TAP_WAVE_BEAT_MS)
			if (beat !== this.lastWaveBeat && elapsed + TAP_WAVE_MS <= this.hoverDwellMs) {
				this.lastWaveBeat = beat
				this.tapWave(this.waveClip, dest.tone)
			}
		}

		// Lemniscate station-keeping — x = sin(w), y = sin(2w) traces a figure-eight, the
		// pattern real hovering hummingbirds use (Royal Society) — plus two low-amplitude
		// sines at an incommensurate rate standing in for corrective darts, so the loop
		// never repeats exactly frame to frame.
		let ox = 0
		let oy = 0
		if (!this.reducedMotion && this.options.motion === 'snitch') oy = Math.sin(now / 520) * 4
		else if (!this.reducedMotion) {
			const w = elapsed * 0.0028
			ox = Math.sin(w) * 4.5 + Math.sin(elapsed * 0.0037) * 1.2
			oy = Math.sin(2 * w) * 2.5 + Math.sin(elapsed * 0.0053) * 0.9
		}
		ctx.cx = dest.x + ox
		ctx.cy = dest.y + oy

		const pulse = this.options.motion === 'snitch' ? 1 : 1 + Math.sin(elapsed * 0.006) * 0.08 // Very subtle 8% scale pulse
		const bank = this.reducedMotion || this.options.motion === 'snitch' ? 0 : Math.cos(elapsed * 0.0028) * 4 // banks into the figure-8's own curve
		this.bird.style.transform = `translate(${ctx.cx - BIRD_RADIUS}px, ${ctx.cy - BIRD_RADIUS}px) rotate(${this.options.motion === 'snitch' ? 0 : ctx.headingDeg + bank}deg) scale(${pulse})`
		if (this.options.motion === 'snitch') {
			this.bird.style.filter = ''
			this.flutter(now)
		} else if (!this.reducedMotion) this.bird.style.filter = `brightness(${pulse})`
		// Anchored to the waypoint, not the bird's own figure-8 wander — the coaching label
		// shouldn't jitter along with it. positionLabel no-ops until a label is showing.
		this.positionLabel(dest)

		if (this.options.stationary) return

		// Hold for the armed dwell - but also wait for next element to be visible
		if (elapsed > this.hoverDwellMs) {
			const nextDest = ctx.destinations[ctx.destIdx + 1]
			const isLastWaypoint = !nextDest

			// Last waypoint (no home): fade out after hovering
			if (isLastWaypoint && !this.hasHome) {
				this.hideWaveClip()
				this.hideLabel()
				this.fadeOutBird()
				return
			}

			if (nextDest?.element && !this.isElementVisible(nextDest.element)) {
				return
			}
			if (nextDest?.element && !this.isInViewport(nextDest.element)) {
				nextDest.element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
			}
			this.hideWaveClip()
			this.hideLabel()
			// Reset trail for new flight segment
			this.resetTrail()
			// Only the index moves — a leg endpoint carried across this transition is already
			// stale by the frame that would read it.
			ctx.destIdx++
			ctx.phaseStart = now
			this.state$.next('flying')
		}
	}

	private flutter(now: number): void {
		for (const wing of this.bird?.querySelectorAll<SVGElement>('.schmancy-hb-wing') ?? []) {
			wing.style.animation = 'none'
			wing.style.opacity = '0.55'
			wing.style.transformBox = 'view-box'
			wing.style.transformOrigin = '12px 12px'
			wing.style.transform = `scaleY(${this.reducedMotion ? 1 : 0.55 + Math.sin(now / 36) * 0.4})`
		}
	}

	/** 0.5 threshold waits for CSS transitions to be halfway done before counting as visible. */
	private isElementVisible(element: HTMLElement): boolean {
		const style = window.getComputedStyle(element)
		const opacity = parseFloat(style.opacity)
		return opacity > 0.5 && style.visibility !== 'hidden' && style.display !== 'none'
	}

	/** 80px padding keeps the bird off the viewport edges. */
	private isInViewport(element: HTMLElement): boolean {
		const r = element.getBoundingClientRect()
		const pad = 80
		return r.bottom > pad && r.top < window.innerHeight - pad && r.right > pad && r.left < window.innerWidth - pad
	}

	/**
	 * Opens the box this waypoint's taps are played back inside.
	 *
	 * `tapWave` leaves clipping to whoever owns the boundary, and here nobody does: the control is a
	 * bare hit zone with no overflow of its own, and a wave sized to reach its far corners stands
	 * taller than the row. So the clip is its own element, inset to the control and carrying the
	 * control's radius — which also gives the departure one node to fade rather than a handful of
	 * waves at unrelated points in their own curves.
	 */
	private openWaveClip(element: HTMLElement, tone: WaypointTone): void {
		const computedStyle = window.getComputedStyle(element)
		if (computedStyle.position === 'static') {
			element.style.position = 'relative'
		}

		const clip = document.createElement('div')
		clip.setAttribute('aria-hidden', 'true')
		clip.style.cssText = `
			position: absolute;
			inset: 0;
			overflow: hidden;
			pointer-events: none;
			z-index: 9999;
			border-radius: ${computedStyle.borderRadius};
		`

		// Nothing expands under reduced motion, so the wash the taps would have swept across the
		// control is held still instead — the half still reads as the marked one. It rides a child
		// rather than the clip's own opacity, which the departure fade owns.
		if (this.reducedMotion) {
			const tint = document.createElement('span')
			tint.style.cssText = `position: absolute; inset: 0; background: ${TAP_WAVE_COLOR[tone]}; opacity: 0.12;`
			clip.appendChild(tint)
		}

		element.appendChild(clip)
		this.waveClip = clip
	}

	/**
	 * One tap, from the middle of the control outward — `tapWave`'s wave in this waypoint's own ink,
	 * at the radius that reaches the clip's furthest corner from its centre, so a single tap washes
	 * the whole half.
	 */
	private tapWave(clip: HTMLDivElement, tone: WaypointTone): void {
		const rect = clip.getBoundingClientRect()
		const radius = Math.hypot(rect.width, rect.height) / 2

		const wave = document.createElement('span')
		wave.style.cssText = `
			position: absolute;
			left: ${rect.width / 2 - radius}px;
			top: ${rect.height / 2 - radius}px;
			width: ${radius * 2}px;
			height: ${radius * 2}px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			background: ${TAP_WAVE_COLOR[tone]};
			filter: blur(6px);
			pointer-events: none;
			transform: scale(0);
			opacity: 0;
		`
		clip.appendChild(wave)

		const anim = wave.animate(
			[
				{ transform: 'scale(0)', opacity: 0 },
				{ opacity: TAP_WAVE_PEAK, offset: 0.22 },
				{ transform: 'scale(1)', opacity: 0 },
			],
			{ duration: TAP_WAVE_MS, easing: TAP_WAVE_EASING },
		)
		anim.finished.then(() => wave.remove()).catch(() => wave.remove())
	}

	private hideWaveClip(): void {
		if (!this.waveClip) return
		// Fades the clip rather than each wave inside it, so a bird leaving mid-tap takes that tap
		// with it on the same curve as the rest of the mark.
		const clip = this.waveClip
		const fadeOut = clip.animate([{ opacity: 1 }, { opacity: 0 }], {
			duration: 300,
			easing: 'ease-out',
			fill: 'forwards',
		})
		fadeOut.onfinish = () => clip.remove()
		this.waveClip = null
	}

	private static readonly LABEL_TRANSFORMS: Record<ResolvedLabelPlacement, string> = {
		top: 'translate(-50%, -100%)',
		bottom: 'translate(-50%, 0)',
		left: 'translate(-100%, -50%)',
		right: 'translate(0, -50%)',
	}

	/** The armed pill's own rendered box, measured once when it is created — what the placement and the viewport clamp are reckoned against. */
	private labelHeight = 0
	private labelWidth = 0

	/**
	 * Floating coaching caption for a waypoint (e.g. "Tap to add"). Cancels/replaces any prior.
	 * Resolves 'auto' placement once here — top vs. bottom by the room left beside the waypoint's own
	 * box, measured against the pill's real height — while the anchor itself is set by positionLabel(),
	 * called separately every frame the waypoint is hovered.
	 */
	private showLabel(text: string, dest: LabelAnchor, side?: ResolvedLabelPlacement, press?: () => void): void {
		this.labelAnimation?.cancel()
		this.labelEl?.remove()

		// A pressable caption is a native button, so its name is its text and Enter and Space press it.
		const label = document.createElement(press ? 'button' : 'div')
		label.textContent = text
		label.className = 'pointer-events-none'
		label.style.cssText = `
			position: absolute;
			left: 0;
			top: 0;
			padding: 4px 10px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			background: var(--schmancy-sys-color-surface-containerHigh, #2a2a2e);
			color: var(--schmancy-sys-color-surface-on, #ffffff);
			font-size: 12px;
			font-weight: 500;
			white-space: nowrap;
			z-index: 100000;
			box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
			opacity: 0;
		`
		// An attend caption is whatever the work reported — a failure's reason runs to a sentence — so
		// it wraps at a reading measure rather than running off the viewport on one line.
		if (side) {
			label.style.whiteSpace = 'normal'
			label.style.maxWidth = 'min(18rem, calc(100vw - 24px))'
			// Its own layer, since it drifts with the bird: text repainted at each fractional offset snaps to
			// the pixel grid and shakes inside its pill.
			label.style.willChange = 'transform'
		}
		if (press) {
			label.setAttribute('type', 'button')
			label.classList.add('schmancy-hb-press')
			label.style.pointerEvents = 'auto'
			label.style.cursor = 'pointer'
			label.style.border = 'none'
			label.style.font = 'inherit'
			label.style.fontSize = '12px'
			label.style.fontWeight = '500'
			label.style.textAlign = 'start'
			label.onclick = press
		}

		this.flyingLayer?.appendChild(label)
		this.labelEl = label
		this.labelText = text
		// One layout read, at arming rather than per frame: the pill's own box decides which side
		// 'auto' takes and how far from an edge it may sit, and both of those are wrong by whatever
		// a guessed height misses.
		this.labelHeight = label.offsetHeight
		this.labelWidth = label.offsetWidth

		// 'auto' weighs the room above the waypoint's own top edge, not above its centre: the centre
		// of a row is under the row's title, so a side picked from there has room for a pill that
		// covers the thing it is captioning.
		if (side) {
			this.resolvedLabelPlacement = side
		} else if (this.labelPlacement === 'auto') {
			const roomAboveBox = dest.y - dest.halfHeight - this.labelGap
			this.resolvedLabelPlacement = roomAboveBox < this.labelHeight ? 'bottom' : 'top'
		} else {
			this.resolvedLabelPlacement = this.labelPlacement ?? 'top'
		}

		this.labelAnimation = label.animate([{ opacity: 0 }, { opacity: 1 }], {
			duration: LABEL_FADE_IN_MS,
			easing: 'ease-out',
			fill: 'forwards',
		})
	}

	/**
	 * Anchors the label pill clear of the waypoint's live box — outside the edge its placement names,
	 * not off the centre point inside it, because that centre on a 72px row is its own title and a
	 * caption sitting over the name of the thing it describes hides what identifies it. A destination
	 * with no element carries the bird's radius for that half-extent, so a fabricated waypoint keeps
	 * the centre-anchored placement it has always had. No-ops until showLabel() has created the pill.
	 *
	 * The anchor rides the transform rather than `left`/`top` so this per-frame write stays on the
	 * compositor: `left`/`top` invalidate layout, which would turn the next tick's destination
	 * measurement into a forced reflow.
	 */
	private positionLabel(dest: LabelAnchor): void {
		if (!this.labelEl) return

		const g = this.labelGap
		// `rise` and `shift` are how far the pill's own top and left edges sit from its anchor, which is
		// what the placement's transform decides: a 'top' pill hangs its whole height above the anchor
		// and half its width to either side of it, a 'left' pill its whole width and half its height.
		const { left, top, rise, shift } = {
			top: { left: dest.x, top: dest.y - dest.halfHeight - g, rise: this.labelHeight, shift: this.labelWidth / 2 },
			bottom: { left: dest.x, top: dest.y + dest.halfHeight + g, rise: 0, shift: this.labelWidth / 2 },
			left: { left: dest.x - dest.halfWidth - g, top: dest.y, rise: this.labelHeight / 2, shift: this.labelWidth },
			right: { left: dest.x + dest.halfWidth + g, top: dest.y, rise: this.labelHeight / 2, shift: 0 },
		}[this.resolvedLabelPlacement]

		// A caption is only a caption where it can be read, so a waypoint close enough to an edge that
		// the pill would overhang it holds the pill inside instead, the same gap off that edge. 'auto'
		// picks the roomier side first and this is what is left when even that side is short; across the
		// pill's length it is the whole answer, because no placement chooses that axis — a wide caption
		// is centred on its half wherever that half happens to sit. The lower bound is applied last, so
		// a caption too long for the viewport spills off the end the reader finishes at rather than the
		// one they start from.
		const heldTop = Math.max(Math.min(top, window.innerHeight - (this.labelHeight - rise) - g), rise + g)
		const heldLeft = Math.max(Math.min(left, window.innerWidth - (this.labelWidth - shift) - g), shift + g)

		this.labelEl.style.transform = `translate(${heldLeft}px, ${heldTop}px) ${HummingbirdDirective.LABEL_TRANSFORMS[this.resolvedLabelPlacement]}`
	}

	private hideLabel(): void {
		if (this.labelAnimation) {
			this.labelAnimation.cancel()
			this.labelAnimation = null
		}
		if (this.labelEl) {
			// Fade out then remove
			const fadeOut = this.labelEl.animate([{ opacity: 1 }, { opacity: 0 }], {
				duration: LABEL_FADE_OUT_MS,
				easing: 'ease-out',
				fill: 'forwards',
			})
			const el = this.labelEl
			// A leaving caption is no longer something to press.
			el.style.pointerEvents = 'none'
			el.inert = true
			fadeOut.onfinish = () => el.remove()
			this.labelEl = null
		}
	}

	/**
	 * A working line giving way to the next, in the pill already standing: the outgoing words fade
	 * where they stood as the new ones fade in over them, and the pill's own box eases from one
	 * line's size to the other's, so its anchored edge and centre never move. Both lines are held at
	 * their own laid-out size, out of flow, while the box between them tweens; the new one returns to
	 * flow once the box has reached it. Under reduced motion the words are simply replaced.
	 */
	private relabel(text: string): void {
		const pill = this.labelEl
		const before = this.labelText
		if (!pill || text === before) return
		this.labelText = text
		if (this.reducedMotion) {
			pill.textContent = text
			this.labelHeight = pill.offsetHeight
			this.labelWidth = pill.offsetWidth
			return
		}
		// Read mid-tween, before it is cancelled, so a line arriving during a swap starts from the box on screen.
		const { width: fromWidth, height: fromHeight } = getComputedStyle(pill)
		this.labelSwap?.cancel()
		const leaving = document.createElement('span')
		leaving.textContent = before
		const incoming = document.createElement('span')
		incoming.textContent = text
		pill.replaceChildren(incoming)
		this.labelHeight = pill.offsetHeight
		this.labelWidth = pill.offsetWidth
		const { width: toWidth, height: toHeight } = getComputedStyle(pill)
		const held = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);'
		incoming.style.cssText = `${held}width:${toWidth}`
		leaving.style.cssText = `${held}width:${fromWidth}`
		pill.append(leaving)
		pill.style.overflow = 'hidden'
		// The two lines overlap only in the middle of the swap: the old one is gone before the box has
		// shrunk far into it, the new one arrives once the box has nearly grown to it, so the box's edge
		// never clips words that are legible.
		leaving.animate([{ opacity: 1 }, { opacity: 0, offset: 0.6 }, { opacity: 0 }], { duration: LABEL_SWAP_MS, fill: 'forwards' })
		incoming.animate([{ opacity: 0 }, { opacity: 0, offset: 0.4 }, { opacity: 1 }], { duration: LABEL_SWAP_MS })
		const swap = pill.animate(
			[
				{ width: fromWidth, height: fromHeight },
				{ width: toWidth, height: toHeight },
			],
			{ duration: LABEL_SWAP_MS, easing: 'ease-out' },
		)
		this.labelSwap = swap
		swap.onfinish = () => {
			incoming.removeAttribute('style')
			leaving.remove()
			pill.style.overflow = ''
			if (this.labelSwap === swap) this.labelSwap = null
		}
	}

	private fadeOutBird(): void {
		if (!this.bird) return

		// idle prevents tickHovering from being called again while fading
		this.state$.next('idle')

		this.bird.animate(
			[
				{ opacity: 1, transform: this.bird.style.transform },
				{ opacity: 0, transform: this.bird.style.transform },
			],
			{ duration: BIRD_EXIT_MS, easing: 'ease-out', fill: 'forwards' },
		).onfinish = () => {
			this.state$.next('done')
		}
	}

	/**
	 * The `playing: false` teardown. Its only entry is update()'s !shouldPlay branch, which a host that
	 * is still connected and re-rendering takes — an unmount goes to disconnected() → cleanup() and never
	 * arrives here — so every teardown on this path has someone watching, and a reader who dismissed the
	 * coach is watching the control they just pressed. The bird leaves it rather than parking on it.
	 */
	private fadeOutAndCleanup(): void {
		if (!this.bird) {
			this.cleanup()
			return
		}

		if (this.rafId) {
			cancelAnimationFrame(this.rafId)
			this.rafId = 0
		}
		// The visibility gate can restart the loop inside this exit; idle parks the tick, so it cannot
		// resume the hover or advance a waypoint underneath the fade.
		this.state$.next('idle')

		// Reset before this fade takes over — a cancel mid-absorption can catch the bird at
		// whatever fractional opacity tickAbsorbed's per-frame write last left it at.
		this.bird.style.opacity = '1'

		const bird = this.bird
		this.attendExit?.cancel()
		this.attendExit = null
		const birdAnim = this.animateExit(bird)
		this.attendExit = birdAnim
		// Called back from here, the bird grows no further and says its line again.
		this.attendLaunching = false
		this.workingLine = ''

		if (this.trailCanvas) {
			this.trailCanvas.animate([{ opacity: 1 }, { opacity: 0 }], {
				duration: BIRD_EXIT_MS,
				easing: 'ease-out',
				fill: 'forwards',
			})
		}

		this.hideWaveClip()
		this.hideLabel()

		birdAnim.onfinish = () => {
			// This fade outlives its own flight: a `playing: false` teardown plus a fresh flight started
			// inside it would otherwise have the stale animation tear the new bird down mid-air.
			if (this.bird !== bird) return
			this.cleanup()
		}
	}

	/**
	 * The departure composes onto the bird's live transform, so it darts along its own body axis
	 * rather than up the screen — the loop is stopped and cannot supply motion, so the exit carries
	 * its own.
	 */
	private animateExit(bird: HTMLElement): Animation {
		const restingTransform = bird.style.transform
		return bird.animate(
			[
				{ opacity: 1, transform: restingTransform },
				{ opacity: 0, transform: `${restingTransform} translateY(-14px) scale(0.7)` },
			],
			{ duration: BIRD_EXIT_MS, easing: 'ease-out', fill: 'forwards' },
		)
	}

	/**
	 * Starts a leg of the attend flight from wherever the bird is now: to the nearest point of the
	 * loop around the surface, which it then circles (working) or holds (done, failed) — or, flying
	 * `on` it, to the wander's point over it. Whatever the last status left standing — its caption, a
	 * pressable bird — goes with it, but for a working line, which rides the leg beside the bird.
	 */
	private beginAttendLeg(now: number): void {
		const ctx = this.context
		const around = this.options.attend?.around ?? this.element
		if (!ctx || !around || !this.bird) return
		const rect = boxOf(around)
		// On the surface the figure-8 keeps running across legs, so a leg starts from the path under it.
		const from = this.options.attend?.flight === 'on' ? this.attendPath : { x: ctx.cx, y: ctx.cy }
		let nearest = Infinity
		if (this.options.attend?.flight === 'on') {
			const point = wanderPoint(inView(rect), this.wander)
			nearest = Math.hypot(point.x - from.x, point.y - from.y)
		} else {
			const perimeter = loopLength(rect)
			for (let i = 0; i < 96; i++) {
				const point = loopPoint(rect, (perimeter * i) / 96)
				const distance = Math.hypot(point.x - from.x, point.y - from.y)
				if (distance < nearest) {
					nearest = distance
					this.attendStep = (perimeter * i) / 96
				}
			}
		}
		this.attendFrom = { x: from.x - rect.left, y: from.y - rect.top }
		this.attendLegPx = nearest
		// A status change on the surface leaves the bird where it is: no leg, so no snitch arc to wait out.
		this.attendLegMs =
			nearest < 1 || (this.options.attend?.flight === 'on' && this.reducedMotion)
				? 0
				: this.options.motion === 'snitch'
					? SNITCH_LEG_MS
					: (nearest / this.speed) * 1000
		this.attendFrame = now
		this.attendArrived = false
		ctx.phaseStart = now
		if (this.attendStatus?.kind !== 'working') this.workingLine = ''
		if (!this.workingLine) this.hideLabel()
		this.bird.style.pointerEvents = ''
		this.bird.style.cursor = ''
		this.bird.onclick = null
	}

	/**
	 * One frame of the attend flight. The surface is re-measured every frame for the same reason a
	 * waypoint is — the bird lives in the top layer, and the surface moves under it for reasons no
	 * event announces. Working advances the bird along the loop; a reported status holds it at the
	 * loop point it reached, hovering, with its caption opening away from the surface.
	 */
	private tickAttending(now: number): void {
		const ctx = this.context
		const bird = this.bird
		const attend = this.options.attend
		const status = this.attendStatus
		if (!ctx || !bird) return
		if (!attend || !status) {
			this.hideLabel()
			this.departAttend(bird)
			return
		}

		// Read each frame, as the press may be named while the bird flies, from its first frame: a bird just launched
		// is already over what is beneath it. A `done` or `failed` clears it with the leg.
		const workingPress = status.kind === 'working' ? attend.workingPress : undefined
		if (status.kind === 'working' && Boolean(workingPress) !== Boolean(bird.onclick)) {
			bird.style.pointerEvents = workingPress ? 'auto' : ''
			bird.style.cursor = workingPress ? 'pointer' : ''
			bird.onclick = workingPress ? () => this.options.attend?.workingPress?.onClick() : null
		}

		const snitch = this.options.motion === 'snitch'
		const bob = snitch && !this.reducedMotion ? Math.sin(now / 520) * 4 : 0
		// Growing at its start point on the leg's own clock, so a pause off-screen resumes the growth
		// where it stopped; the leg it then begins reads whatever status has arrived meanwhile.
		if (this.attendLaunching) {
			const g = Math.min(1, (now - ctx.phaseStart) / hummingbirdLaunch.duration)
			bird.style.transform = `translate(${ctx.cx - BIRD_RADIUS}px, ${ctx.cy - BIRD_RADIUS + bob}px) scale(${1 - (1 - g) ** 3})`
			if (snitch) this.flutter(now)
			if (g < 1) return
			this.attendLaunching = false
			this.beginAttendLeg(now)
		}

		const on = attend.flight === 'on'
		const rect = boxOf(attend.around ?? this.element ?? bird)
		// Over a surface reaching out of the window the bird keeps to what shows, so it and its caption stay together.
		const seen = on ? inView(rect) : rect
		const frameMs = Math.min(now - this.attendFrame, ATTEND_FRAME_CAP_MS)
		this.attendFrame = now
		if (status.kind === 'working' && !this.reducedMotion) {
			if (on) advanceWander(seen, this.wander, frameMs)
			else this.attendStep += (this.speed * frameMs) / 1000
		}
		const centre = { x: seen.left + seen.width / 2, y: seen.top + seen.height / 2 }
		const over = this.reducedMotion ? centre : wanderPoint(seen, this.wander)
		// On the surface, a caption opens off whichever of its top and bottom edges the bird is nearer.
		const point = on
			? { ...over, side: over.y <= centre.y ? ('top' as const) : ('bottom' as const) }
			: loopPoint(rect, this.attendStep)
		const elapsed = now - ctx.phaseStart
		const t = this.attendArrived || this.attendLegMs === 0 ? 1 : Math.min(1, elapsed / this.attendLegMs)
		// The snitch flies its waypoint legs' eased arc, its lift capped by the leg so a bird already
		// beside the loop settles onto it instead of climbing.
		const e = snitch ? 1 - (1 - t) ** 3 : t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
		const lift = snitch && !this.reducedMotion ? Math.sin(t * Math.PI) * Math.min(SNITCH_LIFT_PX, this.attendLegPx) : 0
		const fromX = rect.left + this.attendFrom.x
		const fromY = rect.top + this.attendFrom.y
		const pathX = fromX + (point.x - fromX) * e
		const pathY = fromY + (point.y - fromY) * e - lift
		// A caption stands off the bird's path, not the figure-8 or bob riding it, so it glides where the bird wiggles.
		const beside: LabelAnchor = { x: pathX, y: pathY, halfWidth: BIRD_RADIUS, halfHeight: BIRD_RADIUS }
		const other = (edge: ResolvedLabelPlacement) => (edge === 'top' ? 'bottom' : 'top')
		const roomy = (edge: ResolvedLabelPlacement) =>
			(edge === 'top' ? pathY : window.innerHeight - pathY) - BIRD_RADIUS >= this.labelHeight + 2 * this.labelGap

		if (t >= 1 && !this.attendArrived) {
			this.attendArrived = true
			ctx.phaseStart = now
			if (status.kind !== 'working') {
				this.showLabel(status.label, beside, point.side, status.kind === 'failed' ? () => this.options.attend?.onClick?.() : undefined)
				if (on && !roomy(point.side) && roomy(other(point.side))) this.resolvedLabelPlacement = other(point.side)
			}
			if (status.kind === 'failed') {
				bird.style.pointerEvents = 'auto'
				bird.style.cursor = 'pointer'
				bird.onclick = () => this.options.attend?.onClick?.()
			}
		}

		// The working line floats with the bird for its reading time, then the bird works on without it.
		// It opens towards the surface edge the bird is nearer and keeps that side, changing only when it
		// has run out of viewport there and the other side has some, so a drift never tosses it; changing
		// re-shows it there, the two pills crossfading. A pressable line holding focus stays as it is, as
		// re-showing it or letting it go would drop that focus on the page.
		if (status.kind === 'working' && (this.attendArrived || this.labelEl)) {
			if (status.label && status.label !== this.workingLine) {
				this.workingLine = status.label
				this.workingLineMs = 0
			} else this.workingLineMs += frameMs
			if (!status.label || (this.workingLineMs > readingTimeMs(status.label) && !this.labelEl?.matches(':focus-visible'))) this.hideLabel()
			else {
				const shown = this.labelEl ? this.resolvedLabelPlacement : null
				const side = shown ?? (point.y <= centre.y ? 'top' : 'bottom')
				const edge = this.labelEl?.matches(':focus') || roomy(side) || !roomy(other(side)) ? side : other(side)
				if (edge === shown && Boolean(workingPress) === (this.labelEl?.tagName === 'BUTTON')) this.relabel(status.label)
				else {
					this.hideLabel()
					this.showLabel(status.label, beside, edge, workingPress ? () => this.options.attend?.workingPress?.onClick() : undefined)
					if (!workingPress) this.labelEl?.setAttribute('aria-hidden', 'true')
					if (!roomy(edge) && roomy(other(edge))) this.resolvedLabelPlacement = other(edge)
				}
				if (workingPress && this.labelEl?.getAttribute('aria-label') !== workingPress.name) this.labelEl?.setAttribute('aria-label', workingPress.name)
			}
		}

		// The figure-8 the waypoint hover keeps — around the surface on the hover's own clock from
		// arrival, on it on the page's clock throughout, so the leg, the wander and the report flow into
		// one another without a seam. The snitch keeps its upright bob instead, as between waypoints.
		const hover = !this.reducedMotion && !snitch && (on || (this.attendArrived && status.kind !== 'working'))
		const w = (on ? now : now - ctx.phaseStart) * 0.0028
		const x = pathX + (hover ? Math.sin(w) * 4.5 : 0)
		const y = pathY + (hover ? Math.sin(2 * w) * 2.5 : 0)
		// Heading follows the path, never the figure-8 riding it; on the surface the drift is slow
		// enough that a frame's step is often under a pixel.
		if ((on || !hover) && Math.hypot(pathX - this.attendPath.x, pathY - this.attendPath.y) > (on ? 0.05 : 0.5)) {
			const delta =
				((Math.atan2(pathY - this.attendPath.y, pathX - this.attendPath.x) * (180 / Math.PI) - ctx.headingDeg + 540) % 360) - 180
			ctx.headingDeg += delta * HEADING_SMOOTHING
		}
		this.attendPath = { x: pathX, y: pathY }
		ctx.cx = x
		ctx.cy = y

		const breathe = this.reducedMotion || snitch ? 1 : 1 + Math.sin(now * 0.0016) * 0.04
		bird.style.transform = `translate(${x - BIRD_RADIUS}px, ${y - BIRD_RADIUS + bob}px) rotate(${snitch ? 0 : ctx.headingDeg}deg) scale(${breathe})`
		if (snitch) {
			bird.style.filter = ''
			this.flutter(now)
		} else if (!this.reducedMotion) bird.style.filter = `brightness(${breathe})`
		this.positionLabel(beside)

		if (status.kind === 'done' && this.attendArrived && now - ctx.phaseStart > captionDwellMs(status.label)) {
			this.hideLabel()
			this.departAttend(bird)
		}
	}

	/** The done bird leaves; `hummingbird-complete` fires once it is gone, as for any finished flight. */
	private departAttend(bird: HTMLElement): void {
		this.state$.next('idle')
		const exit = this.animateExit(bird)
		this.attendExit = exit
		exit.onfinish = () => {
			if (this.attendExit !== exit) return
			this.attendExit = null
			this.state$.next('done')
		}
	}

	private tickSpiraling(now: number): void {
		if (!this.context || !this.bird) return

		const ctx = this.context
		this.trackBlackHoleToHome(ctx)
		const elapsed = now - ctx.phaseStart
		const dx = ctx.bx - ctx.cx
		const dy = ctx.by - ctx.cy
		const d = Math.hypot(dx, dy)

		if (d > 8) {
			// Smoother acceleration as it gets closer (gravitational pull)
			const gravitationalPull = Math.pow(1 - d / 150, 2) * 180 + 40
			const speed = Math.min(gravitationalPull, d * 0.15)
			const angle = Math.atan2(dy, dx)

			// Elegant spiral motion - tighter as it gets closer
			const spiralIntensity = Math.min(1, d / 60) * 0.015
			const spiralAngle = elapsed * 0.012
			const spiral = Math.sin(spiralAngle) * d * spiralIntensity

			ctx.cx += Math.cos(angle) * speed * 0.016 + Math.cos(angle + Math.PI / 2) * spiral
			ctx.cy += Math.sin(angle) * speed * 0.016 + Math.sin(angle + Math.PI / 2) * spiral

			const scale = Math.max(0.2, d / 120)
			const rotation = elapsed * 0.3
			this.bird.style.transform = `translate(${ctx.cx - BIRD_RADIUS}px, ${ctx.cy - BIRD_RADIUS}px) rotate(${rotation}deg) scale(${scale})`

			// Add subtle glow intensification as it approaches
			const glowIntensity = 1 - d / 100
			if (glowIntensity > 0.3) {
				this.bird.style.filter = `brightness(${1 + glowIntensity * 0.5}) drop-shadow(0 0 ${glowIntensity * 8}px ${this.currentColor})`
			}
		} else {
			// Transition to absorbed
			ctx.phaseStart = now
			this.startHostDistortion()
			this.state$.next('absorbed')
		}
	}

	/** Store home element for distortion effect */
	private homeElement: HTMLElement | null = null

	private startHostDistortion(): void {
		if (!this.context) return

		const homeDest = this.context.destinations[this.context.destinations.length - 1]
		if (!homeDest?.element) return

		this.homeElement = homeDest.element
		const el = this.homeElement
		const color = this.currentColor
		const originalTransform = el.style.transform || ''

		// Gravitational tremor: rapid oscillations that build in intensity as the
		// home element is shaken by the energy being absorbed into the black hole.
		el.animate(
			[
				{ transform: `${originalTransform} translate(0, 0) scale(1) rotate(0deg)`, filter: 'brightness(1)' },
				{
					transform: `${originalTransform} translate(-2px, 1px) scale(0.992) rotate(-0.3deg)`,
					filter: 'brightness(1.05)',
					offset: 0.05,
				},
				{
					transform: `${originalTransform} translate(3px, -1px) scale(1.005) rotate(0.4deg)`,
					filter: 'brightness(1.1)',
					offset: 0.1,
				},
				{
					transform: `${originalTransform} translate(-1px, 2px) scale(0.995) rotate(-0.2deg)`,
					filter: `brightness(1.15) drop-shadow(0 0 5px ${color})`,
					offset: 0.15,
				},
				{
					transform: `${originalTransform} translate(2px, 0px) scale(1.008) rotate(0.5deg)`,
					filter: 'brightness(1.2)',
					offset: 0.2,
				},
				{
					transform: `${originalTransform} translate(-3px, -2px) scale(0.988) rotate(-0.6deg)`,
					filter: `brightness(1.25) drop-shadow(0 0 8px ${color})`,
					offset: 0.25,
				},
				{
					transform: `${originalTransform} translate(1px, 3px) scale(1.01) rotate(0.3deg)`,
					filter: 'brightness(1.3)',
					offset: 0.3,
				},
				{
					transform: `${originalTransform} translate(-2px, -1px) scale(0.985) rotate(-0.4deg)`,
					filter: `brightness(1.35) drop-shadow(0 0 12px ${color})`,
					offset: 0.35,
				},
				{
					transform: `${originalTransform} translate(4px, 1px) scale(1.015) rotate(0.7deg)`,
					filter: 'brightness(1.4)',
					offset: 0.4,
				},
				{
					transform: `${originalTransform} translate(-1px, 2px) scale(0.99) rotate(-0.5deg)`,
					filter: `brightness(1.35) drop-shadow(0 0 15px ${color})`,
					offset: 0.45,
				},
				{
					transform: `${originalTransform} translate(2px, -2px) scale(1.012) rotate(0.4deg)`,
					filter: 'brightness(1.3)',
					offset: 0.5,
				},
				{
					transform: `${originalTransform} translate(-3px, 1px) scale(0.993) rotate(-0.3deg)`,
					filter: `brightness(1.25) drop-shadow(0 0 10px ${color})`,
					offset: 0.55,
				},
				{
					transform: `${originalTransform} translate(1px, -1px) scale(1.006) rotate(0.2deg)`,
					filter: 'brightness(1.2)',
					offset: 0.6,
				},
				{
					transform: `${originalTransform} translate(-1px, 1px) scale(0.997) rotate(-0.15deg)`,
					filter: 'brightness(1.15)',
					offset: 0.7,
				},
				{
					transform: `${originalTransform} translate(1px, 0px) scale(1.003) rotate(0.1deg)`,
					filter: 'brightness(1.1)',
					offset: 0.8,
				},
				{
					transform: `${originalTransform} translate(0px, -1px) scale(0.999) rotate(-0.05deg)`,
					filter: 'brightness(1.05)',
					offset: 0.9,
				},
				{ transform: `${originalTransform} translate(0, 0) scale(1) rotate(0deg)`, filter: 'brightness(1)' },
			],
			{
				duration: 500,
				easing: 'linear',
			},
		)
	}

	private tickAbsorbed(now: number): void {
		if (!this.context) return

		const ctx = this.context
		// Ahead of the bird guard: the collapse stays on screen for most of a second after the
		// bird is gone, and it tracks on this same measurement.
		this.trackBlackHoleToHome(ctx)
		if (!this.bird) return

		const elapsed = now - ctx.phaseStart
		const duration = 500
		const t = Math.min(1, elapsed / duration)

		// Exponential ease-in for dramatic acceleration into the singularity
		const e = t * t * t

		// Spaghettification effect - stretch vertically while shrinking horizontally
		const scaleX = 0.2 * (1 - e * 0.9)
		const scaleY = 0.2 * (1 - e) * (1 + e * 2) // Elongates before disappearing

		// Rapid rotation acceleration
		const rotation = t * t * 1080 // Accelerating spin

		this.bird.style.transform = `translate(${ctx.bx - BIRD_RADIUS}px, ${ctx.by - BIRD_RADIUS}px) rotate(${rotation}deg) scale(${scaleX}, ${scaleY})`
		this.bird.style.opacity = String(Math.max(0, 1 - e * 1.2))
		this.bird.style.filter = `brightness(${1 + e * 3}) blur(${e * 2}px)`

		if (t >= 1) this.collapseBlackHole()
	}

	/** Additional visual elements for enhanced blackhole */
	private accretionDisks: HTMLElement[] = []
	private lensingRing: HTMLElement | null = null
	private eventHorizon: HTMLElement | null = null
	/**
	 * One container for every black-hole visual, so the whole effect follows `home` through a
	 * scroll on a single composited transform instead of ~30 `left`/`top` writes a frame — those
	 * invalidate layout, which would turn the next frame's own measurement into a forced reflow.
	 * Its children keep the viewport coordinates they were spawned at; the container carries the
	 * drift. Its `z-index` is the group's own top member's, because a transformed element is a
	 * stacking context and its children's z-indices no longer compete outside it.
	 */
	private blackHoleLayer: HTMLElement | null = null
	/** Viewport point the effect was spawned at — the origin its container's transform is measured from. */
	private blackHoleOrigin = { x: 0, y: 0 }

	private createBlackHole(x: number, y: number): void {
		const color = this.currentColor

		const layer = document.createElement('div')
		layer.className = 'pointer-events-none'
		layer.style.cssText = `
			position: absolute;
			left: 0;
			top: 0;
			width: 0;
			height: 0;
			z-index: 10000;
			will-change: transform;
		`
		this.flyingLayer?.appendChild(layer)
		this.blackHoleLayer = layer
		this.blackHoleOrigin = { x, y }

		// 1. Outer gravitational lensing ring - cosmic teal/purple glow
		const lensing = document.createElement('div')
		lensing.className = 'pointer-events-none schmancy-hb-fx-loop'
		lensing.style.cssText = `
			position: absolute;
			left: ${x}px;
			top: ${y}px;
			width: 100px;
			height: 100px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			border: 1px solid ${COSMIC_COLORS.oiiiDim};
			background: radial-gradient(circle, transparent 40%, ${COSMIC_COLORS.deepPurple} 70%, transparent 100%);
			box-shadow:
				inset 0 0 25px ${COSMIC_COLORS.oiiiDim},
				0 0 40px ${COSMIC_COLORS.deepPurple},
				0 0 60px ${hexToRgba(color, 0.15)};
			transform: translate(-50%, -50%) rotateX(85deg) scale(0);
			z-index: 9994;
			will-change: transform, opacity;
			animation: hb-lensing 2s ease-in-out infinite;
		`
		lensing.animate(
			[
				{ transform: 'translate(-50%, -50%) rotateX(85deg) scale(0)' },
				{ transform: 'translate(-50%, -50%) rotateX(85deg) scale(1)' },
			],
			{ duration: 600, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'forwards' },
		)
		this.blackHoleLayer?.appendChild(lensing)
		this.lensingRing = lensing

		// 2. Layered accretion disks - cosmic nebula colors (H-alpha crimson, OIII teal, stellar pink)
		const diskConfigs = [
			{ size: 85, speed: 4, color1: COSMIC_COLORS.oiii, color2: COSMIC_COLORS.deepBlue, rotateX: 68, reverse: true },
			{
				size: 68,
				speed: 2.5,
				color1: COSMIC_COLORS.hAlpha,
				color2: COSMIC_COLORS.hAlphaDim,
				rotateX: 72,
				reverse: false,
			},
			{
				size: 52,
				speed: 1.8,
				color1: COSMIC_COLORS.stellar,
				color2: COSMIC_COLORS.stellarDim,
				rotateX: 76,
				reverse: true,
			},
		]

		diskConfigs.forEach((config, i) => {
			const disk = document.createElement('div')
			disk.className = 'pointer-events-none'
			const direction = config.reverse ? '-' : ''
			disk.style.cssText = `
				position: absolute;
				left: ${x}px;
				top: ${y}px;
				width: ${config.size}px;
				height: ${config.size}px;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
				background: conic-gradient(
					from ${i * 45}deg,
					transparent 0%,
					${config.color1} 15%,
					${config.color2} 35%,
					transparent 50%,
					${config.color1} 65%,
					${config.color2} 85%,
					transparent 100%
				);
				transform: translate(-50%, -50%) rotateX(${config.rotateX}deg) scale(0);
				z-index: ${9995 + i};
				will-change: transform;
				filter: blur(${i * 0.3}px);
				mix-blend-mode: screen;
			`
			// Entrance animation then continuous rotation
			disk.animate(
				[
					{ transform: `translate(-50%, -50%) rotateX(${config.rotateX}deg) scale(0) rotate(0deg)` },
					{ transform: `translate(-50%, -50%) rotateX(${config.rotateX}deg) scale(1) rotate(0deg)` },
				],
				{ duration: 400 + i * 100, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'forwards' },
			).onfinish = () => {
				disk.style.transform = `translate(-50%, -50%) rotateX(${config.rotateX}deg) scale(1)`
				disk.animate(
					[
						{ transform: `translate(-50%, -50%) rotateX(${config.rotateX}deg) scale(1) rotate(0deg)` },
						{ transform: `translate(-50%, -50%) rotateX(${config.rotateX}deg) scale(1) rotate(${direction}360deg)` },
					],
					{ duration: config.speed * 1000, easing: 'linear', iterations: Infinity },
				)
			}
			this.blackHoleLayer?.appendChild(disk)
			this.accretionDisks.push(disk)
		})

		// 3. Event horizon - deep void with cosmic glow corona
		const horizon = document.createElement('div')
		horizon.className = 'pointer-events-none schmancy-hb-fx-loop'
		horizon.style.cssText = `
			--hb-color: ${COSMIC_COLORS.hAlpha};
			--hb-color-dim: ${COSMIC_COLORS.oiiiDim};
			position: absolute;
			left: ${x}px;
			top: ${y}px;
			width: 36px;
			height: 36px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			background: radial-gradient(circle,
				${COSMIC_COLORS.singularity} 0%,
				${COSMIC_COLORS.voidBlack} 40%,
				${COSMIC_COLORS.deepPurple} 60%,
				${COSMIC_COLORS.hAlphaDim} 75%,
				transparent 100%
			);
			box-shadow:
				0 0 15px 4px ${COSMIC_COLORS.hAlpha},
				0 0 30px 8px ${COSMIC_COLORS.oiiiDim},
				0 0 50px 15px ${COSMIC_COLORS.deepPurple},
				inset 0 0 15px ${COSMIC_COLORS.voidBlack};
			transform: translate(-50%, -50%) scale(0);
			z-index: 9999;
			will-change: transform, box-shadow;
			animation: hb-horizon-pulse 1.5s ease-in-out infinite;
		`
		horizon.animate(
			[
				{ transform: 'translate(-50%, -50%) scale(0)' },
				{ transform: 'translate(-50%, -50%) scale(1.15)', offset: 0.6 },
				{ transform: 'translate(-50%, -50%) scale(1)' },
			],
			{ duration: 500, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'forwards' },
		)
		this.blackHoleLayer?.appendChild(horizon)
		this.eventHorizon = horizon
		this.blackHole = horizon // Keep reference for backward compatibility

		// 4. Enhanced orbital particles with cosmic colors
		this.addParticles(x, y)
	}

	private addParticles(cx: number, cy: number): void {
		// Cosmic particle colors - alternating between nebula emission colors
		const cosmicParticleColors = [
			{ bg: COSMIC_COLORS.hAlpha, glow: COSMIC_COLORS.hAlphaDim },
			{ bg: COSMIC_COLORS.oiii, glow: COSMIC_COLORS.oiiiDim },
			{ bg: COSMIC_COLORS.stellar, glow: COSMIC_COLORS.stellarDim },
			{ bg: 'rgba(255, 255, 255, 0.9)', glow: COSMIC_COLORS.stellarDim },
		]

		// Create orbital particles that spiral inward elegantly
		const particleCount = 10
		for (let i = 0; i < particleCount; i++) {
			const p = document.createElement('div')
			const startAngle = (i / particleCount) * Math.PI * 2
			const orbitRadius = 45 + (i % 3) * 18
			const size = 2 + (i % 3)
			const duration = 1400 + (i % 3) * 400
			const delay = i * 70
			const colorSet = cosmicParticleColors[i % cosmicParticleColors.length]

			p.className = 'pointer-events-none'
			p.style.cssText = `
				--orbit-radius: ${orbitRadius}px;
				position: absolute;
				width: ${size}px;
				height: ${size}px;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
				background: ${colorSet.bg};
				box-shadow: 0 0 ${size * 4}px ${colorSet.bg}, 0 0 ${size * 8}px ${colorSet.glow};
				left: ${cx}px;
				top: ${cy}px;
				z-index: 9997;
				will-change: transform, opacity;
				transform-origin: center center;
				transform: rotate(${startAngle}rad) translateX(${orbitRadius}px);
				opacity: 0;
			`

			this.blackHoleLayer?.appendChild(p)
			this.particles.push(p)

			// Smooth orbital spiral animation
			p.animate(
				[
					{
						transform: `rotate(${startAngle}rad) translateX(${orbitRadius}px) scale(1)`,
						opacity: 0,
					},
					{
						transform: `rotate(${startAngle}rad) translateX(${orbitRadius}px) scale(1)`,
						opacity: 0.9,
						offset: 0.1,
					},
					{
						transform: `rotate(${startAngle + Math.PI}rad) translateX(${orbitRadius * 0.5}px) scale(0.8)`,
						opacity: 0.7,
						offset: 0.5,
					},
					{
						transform: `rotate(${startAngle + Math.PI * 2}rad) translateX(0px) scale(0)`,
						opacity: 0,
					},
				],
				{
					duration,
					delay,
					easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
					fill: 'forwards',
				},
			).onfinish = () => p.remove()
		}

		// Cosmic light streaks being pulled in - alternating H-alpha and OIII
		const streakColors = [COSMIC_COLORS.hAlpha, COSMIC_COLORS.oiii, COSMIC_COLORS.stellar, COSMIC_COLORS.oiii]
		for (let i = 0; i < 5; i++) {
			const streak = document.createElement('div')
			const angle = (i / 5) * Math.PI * 2 + Math.PI / 10
			const startDist = 65 + Math.random() * 25
			const streakColor = streakColors[i % streakColors.length]

			streak.className = 'pointer-events-none'
			streak.style.cssText = `
				position: absolute;
				width: 2px;
				height: 14px;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
				background: linear-gradient(to bottom, transparent, ${streakColor}, rgba(255,255,255,0.3));
				left: ${cx + Math.cos(angle) * startDist}px;
				top: ${cy + Math.sin(angle) * startDist}px;
				z-index: 9996;
				will-change: transform, opacity;
				transform: rotate(${angle + Math.PI / 2}rad) scale(0);
				opacity: 0;
				mix-blend-mode: screen;
			`

			this.blackHoleLayer?.appendChild(streak)
			this.particles.push(streak)

			streak.animate(
				[
					{
						transform: `rotate(${angle + Math.PI / 2}rad) scale(0)`,
						opacity: 0,
						left: `${cx + Math.cos(angle) * startDist}px`,
						top: `${cy + Math.sin(angle) * startDist}px`,
					},
					{
						transform: `rotate(${angle + Math.PI / 2}rad) scale(1.5)`,
						opacity: 0.85,
						left: `${cx + Math.cos(angle) * startDist * 0.5}px`,
						top: `${cy + Math.sin(angle) * startDist * 0.5}px`,
						offset: 0.3,
					},
					{
						transform: `rotate(${angle + Math.PI / 2}rad) scale(0.5)`,
						opacity: 0,
						left: `${cx}px`,
						top: `${cy}px`,
					},
				],
				{
					duration: 900,
					delay: 150 + i * 120,
					easing: 'cubic-bezier(0.55, 0, 1, 0.45)',
					fill: 'forwards',
				},
			).onfinish = () => streak.remove()
		}
	}

	private collapseBlackHole(): void {
		this.bird?.remove()
		// Also the latch that keeps this from running twice: the loop goes on past here so the
		// collapse keeps tracking, and every tick guards on the bird it no longer has.
		this.bird = null
		this.particles.forEach(p => p.remove())

		if (!this.context) {
			this.state$.next('done')
			return
		}

		const { x: bx, y: by } = this.blackHoleOrigin
		const color = this.currentColor

		// 1. Collapse accretion disks inward rapidly
		this.accretionDisks.forEach((disk, i) => {
			disk.animate(
				[
					{ transform: disk.style.transform, opacity: 1 },
					{ transform: 'translate(-50%, -50%) rotateX(90deg) scale(0)', opacity: 0 },
				],
				{ duration: 200 + i * 50, easing: 'ease-in', fill: 'forwards' },
			).onfinish = () => disk.remove()
		})
		this.accretionDisks = []

		// 2. Lensing ring collapses
		if (this.lensingRing) {
			this.lensingRing.animate(
				[
					{ transform: 'translate(-50%, -50%) rotateX(85deg) scale(1)', opacity: 0.6 },
					{ transform: 'translate(-50%, -50%) rotateX(85deg) scale(0)', opacity: 0 },
				],
				{ duration: 250, easing: 'ease-in', fill: 'forwards' },
			).onfinish = () => this.lensingRing?.remove()
		}

		// 3. Event horizon/blackhole - brief bright flash then collapse
		const bh = this.blackHole
		if (bh) {
			bh.animate(
				[
					{ transform: 'translate(-50%, -50%) scale(1)', filter: 'brightness(1)' },
					{ transform: 'translate(-50%, -50%) scale(1.3)', filter: 'brightness(3)', offset: 0.2 },
					{ transform: 'translate(-50%, -50%) scale(0)', filter: 'brightness(5)', opacity: 0 },
				],
				{ duration: 350, easing: 'cubic-bezier(0.55, 0, 1, 0.45)', fill: 'forwards' },
			).onfinish = () => bh.remove()
		}

		// 4. COSMIC BIG BANG - Multiple expanding shockwave rings with nebula colors
		const cosmicRingColors = [
			{ border: COSMIC_COLORS.stellar, glow: COSMIC_COLORS.stellarDim },
			{ border: COSMIC_COLORS.hAlpha, glow: COSMIC_COLORS.hAlphaDim },
			{ border: COSMIC_COLORS.oiii, glow: COSMIC_COLORS.oiiiDim },
			{ border: COSMIC_COLORS.deepPurple, glow: COSMIC_COLORS.deepBlue },
		]

		for (let i = 0; i < 4; i++) {
			const ring = document.createElement('div')
			const ringColor = cosmicRingColors[i]
			ring.className = 'pointer-events-none'
			ring.style.cssText = `
				position: absolute;
				left: ${bx}px;
				top: ${by}px;
				width: 20px;
				height: 20px;
				border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
				border: ${2.5 - i * 0.4}px solid ${ringColor.border};
				box-shadow:
					0 0 ${18 - i * 3}px ${ringColor.border},
					0 0 ${30 - i * 5}px ${ringColor.glow},
					inset 0 0 ${12 - i * 2}px ${ringColor.glow};
				transform: translate(-50%, -50%) scale(0.5);
				z-index: ${9990 - i};
				will-change: transform, opacity;
				mix-blend-mode: screen;
			`
			this.blackHoleLayer?.appendChild(ring)

			ring.animate(
				[
					{
						transform: 'translate(-50%, -50%) scale(0.5)',
						opacity: 0.95,
						borderWidth: `${2.5 - i * 0.4}px`,
					},
					{
						transform: 'translate(-50%, -50%) scale(2.5)',
						opacity: 0.6,
						borderWidth: `${1.8 - i * 0.3}px`,
						offset: 0.35,
					},
					{
						transform: 'translate(-50%, -50%) scale(6)',
						opacity: 0,
						borderWidth: '0.5px',
					},
				],
				{
					duration: 800 + i * 120,
					delay: i * 60,
					easing: 'cubic-bezier(0, 0.55, 0.45, 1)',
					fill: 'forwards',
				},
			).onfinish = () => ring.remove()
		}

		// 5. Central cosmic flash burst - white core with H-alpha corona
		const flash = document.createElement('div')
		flash.className = 'pointer-events-none'
		flash.style.cssText = `
			position: absolute;
			left: ${bx}px;
			top: ${by}px;
			width: 10px;
			height: 10px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			background: radial-gradient(circle,
				white 0%,
				${COSMIC_COLORS.stellar} 20%,
				${COSMIC_COLORS.hAlpha} 50%,
				${COSMIC_COLORS.oiiiDim} 70%,
				transparent 100%
			);
			transform: translate(-50%, -50%) scale(0);
			z-index: 10000;
			will-change: transform, opacity;
		`
		this.blackHoleLayer?.appendChild(flash)

		flash.animate(
			[
				{ transform: 'translate(-50%, -50%) scale(0)', opacity: 1 },
				{ transform: 'translate(-50%, -50%) scale(5)', opacity: 1, offset: 0.12 },
				{ transform: 'translate(-50%, -50%) scale(8)', opacity: 0 },
			],
			{ duration: 450, easing: 'ease-out', fill: 'forwards' },
		).onfinish = () => flash.remove()

		// 6. Cosmic afterglow nebula - layered emission colors
		const afterglow = document.createElement('div')
		afterglow.className = 'pointer-events-none'
		afterglow.style.cssText = `
			position: absolute;
			left: ${bx}px;
			top: ${by}px;
			width: 50px;
			height: 50px;
			border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
			background: radial-gradient(circle,
				${COSMIC_COLORS.stellarDim} 0%,
				${COSMIC_COLORS.hAlphaDim} 30%,
				${COSMIC_COLORS.oiiiDim} 50%,
				${COSMIC_COLORS.deepPurple} 70%,
				transparent 100%
			);
			transform: translate(-50%, -50%);
			z-index: 9989;
			will-change: opacity;
			mix-blend-mode: screen;
		`
		this.blackHoleLayer?.appendChild(afterglow)

		afterglow.animate(
			[
				{ opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
				{ opacity: 0.6, transform: 'translate(-50%, -50%) scale(1.8)', offset: 0.4 },
				{ opacity: 0, transform: 'translate(-50%, -50%) scale(2.5)' },
			],
			{ duration: 900, easing: 'ease-out', fill: 'forwards' },
		).onfinish = () => {
			afterglow.remove()
			this.state$.next('done')
		}

		// 7. BIG BANG SHOCKWAVE on host element - violent impact then settle
		if (this.homeElement) {
			const el = this.homeElement
			const originalTransform = el.style.transform || ''

			// Phase 1: Violent shockwave impact with shake
			el.animate(
				[
					// Initial impact - sudden expansion with bright flash
					{ transform: `${originalTransform} translate(0, 0) scale(1) rotate(0deg)`, filter: 'brightness(1)' },
					{
						transform: `${originalTransform} translate(0, 0) scale(1.04) rotate(0deg)`,
						filter: `brightness(2) drop-shadow(0 0 30px ${color})`,
						offset: 0.05,
					},
					// Violent shake sequence
					{
						transform: `${originalTransform} translate(-4px, 2px) scale(1.025) rotate(-0.8deg)`,
						filter: `brightness(1.6) drop-shadow(0 0 25px ${color})`,
						offset: 0.1,
					},
					{
						transform: `${originalTransform} translate(5px, -3px) scale(0.98) rotate(1deg)`,
						filter: `brightness(1.4) drop-shadow(0 0 20px ${color})`,
						offset: 0.15,
					},
					{
						transform: `${originalTransform} translate(-3px, -2px) scale(1.02) rotate(-0.6deg)`,
						filter: `brightness(1.3) drop-shadow(0 0 18px ${color})`,
						offset: 0.2,
					},
					{
						transform: `${originalTransform} translate(4px, 3px) scale(0.985) rotate(0.8deg)`,
						filter: `brightness(1.25) drop-shadow(0 0 15px ${color})`,
						offset: 0.25,
					},
					{
						transform: `${originalTransform} translate(-2px, 1px) scale(1.015) rotate(-0.5deg)`,
						filter: `brightness(1.2) drop-shadow(0 0 12px ${color})`,
						offset: 0.3,
					},
					{
						transform: `${originalTransform} translate(3px, -2px) scale(0.99) rotate(0.6deg)`,
						filter: `brightness(1.18) drop-shadow(0 0 10px ${color})`,
						offset: 0.35,
					},
					{
						transform: `${originalTransform} translate(-2px, 2px) scale(1.01) rotate(-0.4deg)`,
						filter: `brightness(1.15) drop-shadow(0 0 8px ${color})`,
						offset: 0.4,
					},
					{
						transform: `${originalTransform} translate(2px, -1px) scale(0.995) rotate(0.4deg)`,
						filter: 'brightness(1.12)',
						offset: 0.45,
					},
					{
						transform: `${originalTransform} translate(-1px, 1px) scale(1.008) rotate(-0.3deg)`,
						filter: 'brightness(1.1)',
						offset: 0.5,
					},
					{
						transform: `${originalTransform} translate(1px, -1px) scale(0.997) rotate(0.25deg)`,
						filter: 'brightness(1.08)',
						offset: 0.55,
					},
					{
						transform: `${originalTransform} translate(-1px, 0px) scale(1.005) rotate(-0.2deg)`,
						filter: 'brightness(1.06)',
						offset: 0.6,
					},
					{
						transform: `${originalTransform} translate(1px, 1px) scale(0.998) rotate(0.15deg)`,
						filter: 'brightness(1.05)',
						offset: 0.65,
					},
					{
						transform: `${originalTransform} translate(0px, -1px) scale(1.003) rotate(-0.1deg)`,
						filter: 'brightness(1.04)',
						offset: 0.7,
					},
					{
						transform: `${originalTransform} translate(-1px, 0px) scale(0.999) rotate(0.08deg)`,
						filter: 'brightness(1.03)',
						offset: 0.75,
					},
					{
						transform: `${originalTransform} translate(0px, 1px) scale(1.002) rotate(-0.05deg)`,
						filter: 'brightness(1.02)',
						offset: 0.8,
					},
					{
						transform: `${originalTransform} translate(0px, 0px) scale(1.001) rotate(0.03deg)`,
						filter: 'brightness(1.01)',
						offset: 0.9,
					},
					{ transform: `${originalTransform} translate(0, 0) scale(1) rotate(0deg)`, filter: 'brightness(1)' },
				],
				{
					duration: 800,
					easing: 'linear',
				},
			)
		}
	}

	/**
	 * Create the canvas for real-time trail rendering
	 */
	private ensureTrailCanvas(): void {
		if (this.trailCanvas || !this.flyingLayer) return

		const canvas = document.createElement('canvas')
		canvas.width = window.innerWidth * window.devicePixelRatio
		canvas.height = window.innerHeight * window.devicePixelRatio
		canvas.style.cssText = `
			position: absolute;
			top: 0;
			left: 0;
			width: ${window.innerWidth}px;
			height: ${window.innerHeight}px;
			pointer-events: none;
			z-index: 9990;
		`
		this.flyingLayer.appendChild(canvas)
		this.trailCanvas = canvas
		this.trailCtx = canvas.getContext('2d')
		if (this.trailCtx) {
			this.trailCtx.scale(window.devicePixelRatio, window.devicePixelRatio)
		}
	}

	/**
	 * Draw trail segment from last position to current position
	 */
	private drawTrail(x: number, y: number): void {
		if (!this.options.showConnections) return
		this.ensureTrailCanvas()
		if (!this.trailCtx || !this.lastTrailPos) {
			this.lastTrailPos = { x, y }
			return
		}

		const ctx = this.trailCtx
		const color = this.currentColor

		// Draw line segment from last position to current
		ctx.beginPath()
		ctx.moveTo(this.lastTrailPos.x, this.lastTrailPos.y)
		ctx.lineTo(x, y)
		ctx.strokeStyle = color
		ctx.lineWidth = 3
		ctx.lineCap = 'round'
		ctx.globalAlpha = 0.6
		ctx.shadowColor = color
		ctx.shadowBlur = 8
		ctx.stroke()
		ctx.globalAlpha = 1
		ctx.shadowBlur = 0

		this.lastTrailPos = { x, y }
	}

	/**
	 * Reset trail position (called when starting new flight segment)
	 */
	private resetTrail(): void {
		this.lastTrailPos = null
	}

	/**
	 * Fade out the entire trail canvas
	 */
	private fadeTrail(): void {
		if (!this.trailCtx) return
		// Gradually fade existing content
		this.trailCtx.globalCompositeOperation = 'destination-out'
		this.trailCtx.fillStyle = 'rgba(0, 0, 0, 0.02)'
		this.trailCtx.fillRect(0, 0, window.innerWidth, window.innerHeight)
		this.trailCtx.globalCompositeOperation = 'source-over'
	}

	private cleanup(): void {
		this.destroyed$.next(true)
		this.initialized = false

		if (this.rafId) {
			cancelAnimationFrame(this.rafId)
			this.rafId = 0
		}

		// Cleanup the waypoint's tap waves
		this.waveClip?.remove()
		this.waveClip = null
		this.lastWaveBeat = -1

		// Cleanup label
		this.labelAnimation?.cancel()
		this.labelEl?.remove()
		this.labelAnimation = null
		this.labelEl = null
		this.labelHeight = 0
		this.labelWidth = 0

		this.attendExit?.cancel()
		this.attendExit = null
		this.attendArrived = false
		this.attendLaunching = false
		this.attendStart = null
		this.workingLine = ''
		this.workingLineMs = 0

		// A replay starts at destination 0 again, and that waypoint has to be able to arm a second time
		this.armedDestIdx = -1
		this.hoverDwellMs = 0
		this.pausedAt = 0

		// Cleanup trail canvas
		this.trailCanvas?.remove()
		this.trailCanvas = null
		this.trailCtx = null
		this.lastTrailPos = null

		// Cleanup enhanced blackhole elements
		this.accretionDisks.forEach(d => d.remove())
		this.accretionDisks = []
		this.lensingRing?.remove()
		this.lensingRing = null
		this.eventHorizon?.remove()
		this.eventHorizon = null
		this.blackHoleLayer?.remove()
		this.blackHoleLayer = null
		this.homeElement = null

		this.releasePress()
		this.bird?.remove()
		this.blackHole?.remove()
		this.particles.forEach(p => p.remove())

		this.bird = null
		this.blackHole = null
		this.particles = []
		this.context = null

		this.flyingLayer?.hidePopover()
		this.flyingLayer?.remove()
		this.flyingLayer = null
	}

	override disconnected(): void {
		// The status is forgotten with the subscription, so the one a reconnect replays lands as news.
		this.attendSource$.next(null)
		this.attendStatus = null
		this.cleanup()
	}

	override reconnected(): void {
		const held = this.heldBack
		this.heldBack = null
		if (held) this.update(held.part, [held.options])
		else this.attendSource$.next(this.options.attend?.status ?? null)
	}
}

export const hummingbird = directive(HummingbirdDirective)
